import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';
import {
    type ApiUserActivityDownloadCsv,
    type SingleSendExclusionAnalyticsResults,
    type UnusedContent,
    type UserActivity,
    type ViewStatistics,
} from './types/analytics';
import { type AnyType } from './types/any';
import {
    LightdashMode,
    type ApiCompiledQueryResults,
    type CreateProjectTableConfiguration,
    type TableSelectionType,
} from './types/api';
import {
    type ApiCreateComment,
    type ApiDeleteComment,
    type ApiGetComments,
} from './types/api/comments';
import { type Email } from './types/api/email';
import { type ApiSuccessEmpty } from './types/api/success';
import { type AudienceMembershipConfig } from './types/audienceMembership';
import { type Audience, type AudienceList } from './types/audiences';
import { type Account } from './types/auth';
import { type ColumnUsageStats } from './types/columnAnalysis';
import {
    type AllIntegrationChannels,
    type ChannelIntegrationDetails,
    type FynoTemplateDetails,
    type SyncedTemplateBasicDetails,
    type TemplateBasicDetails,
    type UtmKeyMapping,
} from './types/communications';
import {
    type Dashboard,
    type DashboardAvailableFilters,
    type DashboardBasicDetails,
    type DashboardSummary,
} from './types/dashboard';
import { type DbtExposure } from './types/dbt';
import { type EmailStatusExpiring } from './types/email';
import {
    type ColumnInfo,
    type CreateSourceEventNameKeyMappingResponse,
    type EventColumn,
    type EventLog,
    type EventLogsStats,
    type EventLogWithoutRawPayload,
    type EventMapperSchema,
    type SourceEventKeyMapping,
} from './types/events';
import { type Explore, type SummaryExplore } from './types/explore';
import {
    DimensionType,
    friendlyName,
    isCustomDimension,
    isDimension,
    isField,
    isFilterableDimension,
    isMetric,
    isTableCalculation,
    type CompiledField,
    type CustomDimension,
    type Dimension,
    type Field,
    type FieldId,
    type FilterableDimension,
    type FilterableField,
    type ItemsMap,
    type Metric,
    type TableCalculation,
} from './types/field';
import { type IntegrationDetails } from './types/integrations';
import {
    type AdditionalMetric,
    type MetricQuery,
    type QueryWarning,
} from './types/metricQuery';
import {
    OrganizationMemberRole,
    type ApiOrganizationMemberProfiles,
    type OrganizationMemberProfile,
} from './types/organizationMemberProfile';
import {
    type CreatePersonalAccessToken,
    type PersonalAccessToken,
} from './types/personalAccessToken';
import type { QuestLinkedChatContext } from './types/questLinkedChat';
import type { QuestMilestoneReport } from './types/questMilestoneReport';
import { type SearchResults } from './types/search';
import { type ShareUrl } from './types/share';
import { type SlackSettings } from './types/slackSettings';
import { type ApiCreateTagResponse } from './types/tags';

import { type AgentTask } from './types/agentTask';
import { type FieldValueSearchResult } from './types/fieldMatch';
import { type DashboardFilters } from './types/filter';
import {
    type GitIntegrationConfiguration,
    type GitRepo,
    type PullRequestCreated,
} from './types/gitIntegration';
import {
    type OpenCodeHealth,
    type OpenCodeMessage,
    type OpenCodeSession,
    type OpenCodeSessionInfo,
} from './types/opencode';
import {
    type DeleteOpenIdentity,
    type OpenIdIdentitySummary,
} from './types/openIdIdentity';
import {
    type AllowedEmailDomains,
    type OnboardingStatus,
    type Organization,
    type OrganizationProject,
    type UpdateAllowedEmailDomains,
} from './types/organization';
import { type ApiTogglePinnedItem, type PinnedItems } from './types/pinning';
import { type ProjectGroupAccess } from './types/projectGroupAccess';
import { type ProjectMemberProfile } from './types/projectMemberProfile';
import { type ProjectMemberRole } from './types/projectMemberRole';
import {
    DbtProjectType,
    ProjectType,
    type BaseProject,
    type CreateWarehouseCredentials,
    type DbtProjectConfig,
    type PipedreamConnectSession,
    type Project,
    type UnsubscribeIdTypes,
    type WarehouseCredentials,
    type WarehouseTypes,
} from './types/projects';
import {
    type ApiQuestsResponse,
    type OpenQuestHumanInput,
    type Quest,
    type QuestActivityLog,
    type QuestHandoff,
    type QuestHumanInput,
    type QuestHumanInputSummary,
    type QuestWait,
} from './types/quest';
import {
    type ApiColumnBasicAnalysisResult,
    type ApiColumnFillRateResult,
    type ApiColumnTopValue,
    type ApiGenerateColumnAiContextResult,
    type ApiGenerateColumnSynonymsResult,
    type ApiGeneratedColumnDescription,
    type ApiRegenerateColumnDescriptionResult,
    type ApiRegenerateColumnLabelResult,
    type ApiUpdateRelationTable,
    type CompileQueryResponse,
    type CreateJoinResponse,
    type CreateRelationResponse,
    type CreateRelationTableResponse,
    type DownloadCsvResponse,
    type GetRelationResponse,
    type GetRelationsResponse,
    type RelationSchemaTableResponse,
    type TableColumnUsageStatsResponse,
} from './types/relation/dtos';
import {
    type CompiledRelation,
    type DimensionConfigurationResult,
    type SummaryRelation,
} from './types/relation/relations';
import { type MostPopularAndRecentlyUpdated } from './types/resourceViewItem';
import { type ResultColumns, type ResultRow } from './types/results';
import {
    type ApiCalculateSubtotalsResponse,
    type ApiCalculateTotalResponse,
    type ChartHistory,
    type ChartVersion,
    type SavedChart,
    type Series,
} from './types/savedCharts';
import {
    type ApiJobScheduledResponse,
    type ApiJobStatusResponse,
    type ApiSchedulersResponse,
    type ApiUserSchedulersSummaryResponse,
    type SchedulerAndTargets,
    type SchedulerJobStatus,
} from './types/scheduler';
import { type ApiSlackChannelsResponse } from './types/slack';
import { SpaceMemberRole, type CreateSpace, type Space } from './types/space';
import { type ApiSshKeyPairResponse } from './types/SshKeyPair';
import { type TableBase } from './types/table';
import {
    type LightdashUser,
    type LoginOptions,
    type LoginWith2FAResult,
    type UserAllowedOrganization,
} from './types/user';
import { type UserWarehouseCredentials } from './types/userWarehouseCredentials';
import { type ValidationResponse } from './types/validation';

import type {
    AiArtifact,
    ApiAiAgentAdminConversationsResponse,
    ApiAiAgentEvaluationRunSummaryListResponse,
    ApiAiAgentThreadCreateResponse,
    ApiAiAgentThreadGenerateTitleResponse,
    ApiAiAgentThreadMessageCreateResponse,
    ApiAiAgentThreadMessageVizQueryResponse,
    ApiAiAgentThreadMessageVizResponse,
    ApiAiAgentThreadResponse,
    ApiAiAgentVerifiedArtifactsResponse,
    ApiAppendInstructionResponse,
    ApiCreateEvaluationResponse,
    ApiGetUserAgentPreferencesResponse,
    ApiUpdateAiOrganizationSettingsResponse,
    ApiUpdateUserAgentPreferencesResponse,
    DecodedEmbed,
    EmbedUrl,
    GeneratedTableCalculation,
} from './ee';
import { type AiEntitySearchResults } from './types/aiEntities';
import { type UserAlertPreferenceResponse } from './types/alerts';
import {
    type ApiGetProjectParametersListResults,
    type ApiGetProjectParametersResults,
} from './types/api/parameters';
import { type ApiGetSpotlightTableConfig } from './types/api/spotlight';
import { type AudienceDestinationSync } from './types/audienceDestinationSyncs';
import type { CampaignList } from './types/campaign';
import {
    type CampaignQaCreateResponse,
    type ContentAnalysisTriggerResponse,
} from './types/campaignQa';
import {
    type ApiCatalogAnalyticsResults,
    type ApiCatalogMetadataResults,
    type ApiGetMetricsTree,
    type ApiMetricsCatalog,
} from './types/catalog';
import { type ApiGetChangeResponse } from './types/changeset';
import {
    type ApiChartAsCodeListResponse,
    type ApiChartAsCodeUpsertResponse,
    type ApiDashboardAsCodeListResponse,
} from './types/coder';
import type {
    AirbyteConnection,
    GetConnectorDestinationResponse,
} from './types/connections';
import {
    type ApiChartContentResponse,
    type ApiContentResponse,
} from './types/content';
import {
    type CustomAttribute,
    type GenerateCustomAttribute,
} from './types/customAttributes';
import { type CustomMaterialisation } from './types/customMaterialisations';
import { type DeliveryControl } from './types/deliveryControl';
import {
    type CreateEntityAPIKeyResponse,
    type EntityApiKey,
} from './types/entityAPIKeys';
import type {
    ApiGroupListResponse,
    Group,
    GroupWithMembers,
} from './types/groups';
import {
    type CoreMemoryResult,
    type DirectivesResult,
    type IntelligenceBlock,
    type IntelligenceBlockList,
    type IntelligenceBlockListWithCreator,
    type IntelligenceBlockWithCreator,
    type PlaybookDebugCreateResult,
    type PlaybookDebugStatus,
    type PlaybookResult,
    type RelatedMemoriesResult,
    type UserMemorySearchResult,
} from './types/intelligenceBlock';
import { type InternalCampaignQARecord } from './types/internalCampaignQa';
import { type JourneyDataSchema } from './types/journeys';
import type { Kpi, KpiPreviewResponse } from './types/kpi';
import {
    type AgentMessage,
    type ColumnDetails,
    type JourneyNodeNameDescriptionResponse,
    type NaturalLanguageSQLResult,
    type TableDescription,
    type TableDetails,
    type UserThreadList,
    type UserThreadMapping,
} from './types/llm';
import type {
    ApiMetricsExplorerQueryResults,
    ApiMetricsExplorerTotalResults,
} from './types/metricsExplorer';
import type { ResultsPaginationMetadata } from './types/paginateResults';
import { type ParametersValuesMap } from './types/parameters';
import { type PivotConfiguration } from './types/pivot';
import {
    type ApiProjectCompileLogResponse,
    type ApiProjectCompileLogsResponse,
} from './types/projectCompileLogs';
import { type ApiPromotionChangesResponse } from './types/promotion';
import { type QueryHistoryStatus } from './types/queryHistory';
import { type CompiledRelationTable } from './types/relation/compiledTables';
import { RelationTableType } from './types/relation/enums';
import { type ApiRenameFieldsResponse } from './types/rename';
import { type SchedulerWithLogs } from './types/schedulerLog';
import { type SenderProfile } from './types/senderProfile';
import { SentryTagKeys, type SentryPackageType } from './types/sentry';
import {
    type ApiCreateSqlChart,
    type ApiCreateVirtualView,
    type ApiGithubDbtWritePreview,
    type ApiSqlChart,
    type ApiSqlRunnerJobStatusResponse,
    type ApiUpdateSqlChart,
    type GroupByColumn,
    type SortBy,
} from './types/sqlRunner';
import { type SrtCatalog, type SrtCatalogList } from './types/srtCatalog';
import {
    type SrtCollection,
    type SrtCollectionList,
} from './types/srtCollection';
import { type SubscriptionCategory } from './types/subscriptionCategory';
import {
    type SubscriptionGroup,
    type SubscriptionGroupWithName,
} from './types/subscriptionGroup';
import {
    type BeeFreeAuthResponse,
    type BeefreeGetAllTemplatesResponse,
    type BeefreeGetTemplateByIdResponse,
    type BeefreeTemplateCategoriesResponse,
    type BeefreeTemplateCategory,
    type BeefreeTemplateCollection,
    type BeefreeTemplateCollectionsResponse,
    type BeefreeTemplateTagsResponse,
    type BrazeExportTemplateResponse,
    type ExternalTemplate,
    type ExtractVariablesResponse,
    type FileUploadResponse,
    type GetAllProviderTemplateResponse,
    type HandlebarNodeType,
    type ProviderTemplateDetails,
    type Template,
    type TemplateContent,
    type TemplateListWithPagination,
} from './types/templates';
import { TimeFrames } from './types/timeFrames';
import {
    type CategoriesWithGroupsAndUserPreference,
    type UserPreference,
    type UserSubscriptionPreference,
} from './types/userSubscriptionPreference';
import { type UserTraits } from './types/userTraits';
import { type View } from './types/views';
import {
    type ApiWarehouseTableFields,
    type WarehouseCatalog,
    type WarehouseTableInfo,
} from './types/warehouse';
import { type SearchWHEntityData } from './types/warehouseCacheSync';
import { convertAdditionalMetric } from './utils/additionalMetrics';
import {
    getDedupedEventTableName,
    getDedupedSubscriptionEventTableName,
    getEventTableName,
    getJourneyEventsTableName,
} from './utils/event';
import { getFields } from './utils/fields';
import { formatItemValue } from './utils/formatting';
import { getItemId, getItemLabelWithoutTableName } from './utils/item';
import { getOrganizationNameSchema } from './utils/organization';
import { getSortmentWarehouseName } from './utils/sortmentUtils';
import type { PivotValuesColumn } from './visualizations/types';

dayjs.extend(utc);
export * from './authorization/buildAccountHelpers';
export {
    defineUserAbility,
    getUserAbilityBuilder,
    JWT_HEADER_NAME,
} from './authorization/index';
export * from './authorization/jwtAbility';
export * from './authorization/parseAccount';
export * from './authorization/roleToScopeMapping';
export * from './authorization/scopes';
export * from './authorization/serviceAccountAbility';
export * from './authorization/types';
export * from './compiler/compilationReport';
export * from './compiler/exploreCompiler';
export * from './compiler/filtersCompiler';
export * from './compiler/lightdashModelConverter';
export * from './compiler/parameters';
export * from './compiler/relationCompiler';
export * from './compiler/translator';
export * from './constants/ai';
export * from './constants/screenshot';
export * from './constants/sessionStorageKeys';
export * from './constants/sqlRunner';
export { default as DbtSchemaEditor } from './dbt/DbtSchemaEditor/DbtSchemaEditor';
export * from './dbt/validation';
export * from './ee';
export * from './notifications/questNotificationUtils';
export * from './pivot/derivePivotConfigFromChart';
export * from './pivot/pivotConfig';
export * from './pivot/pivotQueryResults';
export * from './pivot/utils';
export { default as chartAsCodeSchema } from './schemas/json/chart-as-code-1.0.json';
export { default as dashboardAsCodeSchema } from './schemas/json/dashboard-as-code-1.0.json';
export { default as lightdashDbtYamlSchema } from './schemas/json/lightdash-dbt-2.0.json';
export { default as lightdashProjectConfigSchema } from './schemas/json/lightdash-project-config-1.0.json';
export { default as modelAsCodeSchema } from './schemas/json/model-as-code-1.0.json';
export * from './templating/template';
export * from './types/account';
export * from './types/adminAiReports';
export * from './types/agentChat';
export * from './types/agentTask';
export * from './types/aggregations';
export * from './types/aiAttachment';
export * from './types/aiAudience';
export * from './types/aiContent';
export * from './types/aiCustomAttribute';
export * from './types/aiEntities';
export * from './types/airbyte';
export * from './types/aiStructuredContext';
export * from './types/alerts';
export * from './types/analytics';
export * from './types/any';
export * from './types/api';
export * from './types/api/comments';
export * from './types/api/errors';
export * from './types/api/notifications';
export * from './types/api/paginatedQuery';
export * from './types/api/parameters';
export * from './types/api/share';
export * from './types/api/sort';
export * from './types/api/spotlight';
export * from './types/api/success';
export * from './types/api/uuid';
export * from './types/audienceDestinationSyncs';
export * from './types/audienceMembership';
export * from './types/audiences';
export * from './types/auditLog';
export * from './types/auth';
export * from './types/bigQuerySSO';
export * from './types/blob/blobConfigs';
export * from './types/blob/storageConfigs';
export * from './types/blob/warehouseConfigs';
export * from './types/blobStorage';
export * from './types/campaign';
export * from './types/campaignQa';
export * from './types/CampaignScheduleType';
export * from './types/CampaignStatus';
export * from './types/catalog';
export * from './types/changeset';
export * from './types/coder';
export {
    type ColumnAnalysis,
    type ColumnUsageStats,
    type RelationAnalysisResult,
} from './types/columnAnalysis';
export * from './types/comments';
export * from './types/communications';
export * from './types/conditionalFormatting';
export * from './types/connections';
export * from './types/content';
export * from './types/csv';
export * from './types/customAttributes';
export * from './types/customerIo';
export * from './types/customMaterialisations';
export * from './types/dashboard';
export * from './types/dbt';
export * from './types/deliveryControl';
export * from './types/downloadFile';
export * from './types/email';
export * from './types/engagementAnalytics';
export * from './types/entityAPIKeys';
export * from './types/errors';
export * from './types/events';
export * from './types/explore';
export * from './types/featureFlags';
export * from './types/field';
export * from './types/fieldMatch';
export * from './types/filter';
export * from './types/funnel';
export * from './types/gdrive';
export * from './types/gitIntegration';
export * from './types/groups';
export * from './types/holdoutConfig';
export * from './types/homeFeed';
export * from './types/integrations';
export * from './types/intelligenceBlock';
export * from './types/internalCampaignQa';
export * from './types/job';
export * from './types/journeyApiBlockAnalytics';
export * from './types/journeys';
export * from './types/knex-paginate';
export * from './types/kpi';
export * from './types/lightdashModel';
export { DEFAULT_SPOTLIGHT_CONFIG } from './types/lightdashProjectConfig';
export type {
    LightdashProjectConfig,
    LightdashProjectParameter,
    WarehouseConfig as LightdashWarehouseConfig,
} from './types/lightdashProjectConfig';
export * from './types/llm';
export * from './types/markdown';
export * from './types/mediaLibrary';
export * from './types/metricQuery';
export * from './types/metricsExplorer';
export * from './types/notifications';
export * from './types/oauth';
export * from './types/opencode';
export * from './types/openIdIdentity';
export * from './types/organization';
export * from './types/organizationMemberProfile';
export * from './types/organizationWarehouseCredentials';
export * from './types/paginateResults';
export * from './types/parameters';
export * from './types/periodOverPeriodComparison';
export * from './types/personalAccessToken';
export * from './types/pinning';
export * from './types/pivot';
export * from './types/projectCompileLogs';
export * from './types/projectGroupAccess';
export * from './types/projectMemberProfile';
export * from './types/projectMemberRole';
export * from './types/projects';
export * from './types/promotion';
export * from './types/queryHistory';
export * from './types/quest';
export * from './types/relation/compiledTables';
export * from './types/relation/dtos';
export * from './types/relation/enums';
export * from './types/relation/relations';
export * from './types/relation/tables';
export * from './types/rename';
export * from './types/resourceViewItem';
export * from './types/results';
export * from './types/roles';
export * from './types/savedCharts';
export * from './types/scheduler';
export * from './types/schedulerLog';
export * from './types/schedulerTaskList';
export * from './types/scopes';
export * from './types/search';
export * from './types/senderProfile';
export * from './types/SendgridMigrateTemplateRequest';
export * from './types/sentry';
export * from './types/share';
export * from './types/slack';
export * from './types/slackDm';
export * from './types/slackMirror';
export * from './types/slackSettings';
export * from './types/sortmentAdmin';
export * from './types/space';
export * from './types/spotlightTableConfig';
export * from './types/sqlRunner';
export * from './types/srtAnalytics';
export * from './types/srtCatalog';
export * from './types/srtCollection';
export * from './types/srtNotification';
export * from './types/SshKeyPair';
export * from './types/subscriptionCategory';
export * from './types/subscriptionGroup';
export * from './types/supressionConfig';
export * from './types/table';
export * from './types/tags';
export * from './types/templates';
export * from './types/testProfiles';
export * from './types/timeFrames';
export * from './types/timezone';
export * from './types/user';
export * from './types/userAttributes';
export * from './types/userSubscriptionPreference';
export * from './types/userTraits';
export * from './types/userWarehouseCredentials';
export * from './types/validation';
export * from './types/versionHistory';
export * from './types/views';
export * from './types/warehouse';
export * from './types/warehouseCacheSync';
export * from './types/webhooks';
export * from './types/webPush';
export * from './types/yamlSchema';
export * from './utils/accessors';
export * from './utils/additionalMetrics';
export * from './utils/aiContent/openUiChannelTranslator';
export * from './utils/aiContent/openUiMarkdownTranslator';
export * from './utils/aiContent/openUiProgram';
export * from './utils/aiContent/safeInternalPath';
export * from './utils/api';
export { default as assertUnreachable } from './utils/assertUnreachable';
export * from './utils/audiences';
export * from './utils/campaign';
export * from './utils/catalogMetricsTree';
export * from './utils/changeset';
export * from './utils/charts';
export * from './utils/chartValidation';
export * from './utils/colors';
export * from './utils/conditionalFormatExpressions';
export * from './utils/conditionalFormatting';
export * from './utils/convertCustomDimensionsToYaml';
export * from './utils/convertCustomMetricsToYaml';
export * from './utils/customAttributes';
export * from './utils/customDimensions';
export * from './utils/customExplore';
export * from './utils/dashboard';
export * from './utils/dbt';
export * from './utils/dependencyGraph';
export * from './utils/email';
export * from './utils/event';
export * from './utils/fields';
export * from './utils/filters';
export * from './utils/formatting';
export * from './utils/github';
export * from './utils/i18n/chartAsCode';
export * from './utils/i18n/dashboardAsCode';
export * from './utils/i18n/merge';
export * from './utils/i18n/types';
export * from './utils/item';
export * from './utils/journeyActionSeeds';
export * from './utils/journeyExperiments';
export * from './utils/journeyNaming';
export * from './utils/journeys';
export * from './utils/loadLightdashProjectConfig';
export * from './utils/markdownHelper';
export * from './utils/metricsExplorer';
export * from './utils/oauth';
export * from './utils/organization';
export * from './utils/projectMemberRole';
export * from './utils/promises';
export * from './utils/redactMcpContextTokens';
export * from './utils/sanitizeHtml';
export * from './utils/scheduler';
export * from './utils/searchParams';
export * from './utils/slack';
export * from './utils/sleep';
export * from './utils/slugs';
export * from './utils/sortmentUtils';
export * from './utils/sqlValidation';
export * from './utils/subtotals';
export * from './utils/tags';
export * from './utils/time';
export * from './utils/timeFrames';
export * from './utils/userSubscription';
export * from './utils/utils';
export * from './utils/virtualView';
export * from './utils/warehouse';
export * from './visualizations/CartesianChartDataModel';
export * from './visualizations/helpers/getCartesianAxisFormatterConfig';
export * from './visualizations/helpers/styles/axisStyles';
export * from './visualizations/helpers/styles/barChartStyles';
export * from './visualizations/helpers/styles/gridStyles';
export * from './visualizations/helpers/styles/legendStyles';
export * from './visualizations/helpers/styles/pieChartStyles';
export * from './visualizations/helpers/styles/referenceLineStyles';
export * as vizThemeColors from './visualizations/helpers/styles/themeColors';
export * from './visualizations/helpers/styles/tooltipStyles';
export * from './visualizations/helpers/styles/valueLabelStyles';
export * from './visualizations/helpers/tooltipFormatter';
export * from './visualizations/helpers/valueFormatter';
export * from './visualizations/PieChartDataModel';
export * from './visualizations/TableDataModel';
export * from './visualizations/types';
export * from './visualizations/types/IResultsRunner';

export const validateEmail = (email: string): boolean => {
    if (/\s/.test(email)) {
        return false;
    }

    const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(String(email).toLowerCase());
};

export const getEmailSchema = () =>
    z
        .string()
        .refine((email) => validateEmail(email), {
            message: 'Email address is not valid',
        })
        .refine((email) => !/\s/.test(email), {
            message: 'Email address must not contain whitespaces',
        });

export const getPasswordSchema = () =>
    z
        .string()
        .min(8, { message: 'must be at least 8 characters long' })
        .regex(/[a-z]/, {
            message: 'must contain one lowercase letter',
        })
        .regex(/[A-Z]/, {
            message: 'must contain one uppercase letter',
        })
        .regex(/[a-zA-Z]/, { message: 'must contain a letter' })
        .regex(/\d|\W|_/, { message: 'must contain a number or symbol' });

export const validatePassword = (password: string): boolean =>
    getPasswordSchema().safeParse(password).success;

export const hasIntersection = (tags: string[], tags2: string[]): boolean => {
    const intersection = tags.filter((value) => tags2.includes(value));
    return intersection.length > 0;
};

export const toggleArrayValue = <T = string>(
    initialArray: T[],
    value: T,
): T[] => {
    const array = [...initialArray];
    const index = array.indexOf(value);
    if (index === -1) {
        array.push(value);
    } else {
        array.splice(index, 1);
    }
    return array;
};

export const replaceStringInArray = (
    arrayToUpdate: string[],
    valueToReplace: string,
    newValue: string,
) =>
    arrayToUpdate.map((value) => (value === valueToReplace ? newValue : value));

export type SqlResultsRow = { [columnName: string]: unknown };
export type SqlResultsField = { name: string; type: string }; // TODO: standardise column types
export type SqlQueryResults = {
    fields: SqlResultsField[]; // TODO: standard column types
    rows: SqlResultsRow[];
};

export function hexToRGB(hex: string, alpha: number | undefined): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    if (alpha !== undefined) {
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    return `rgb(${r}, ${g}, ${b})`;
}

// Seeds
export const SEED_ORG_1 = {
    organization_uuid: '66441723-8b95-4033-950a-cc0801f83052',
    organization_name: 'shopflo staging',
    is_internal: false,
};

export const SEED_ORG_1_ADMIN = {
    user_uuid: '46d1eae7-e4cc-41e4-b45a-f0b170b3a6d6',
    first_name: 'Demo User',
    last_name: 'Staging',
    is_marketing_opted_in: false,
    is_tracking_anonymized: false,
    is_setup_complete: true,
    is_active: true,
    role: 'admin',
};

export const isFeatureUser = (userEmail: string) => {
    const allowed = [
        'ankur.banga@sortment.com',
        'ishan@shopflo.com',
        'demo@sortment.com',
        'priy@shopflo.com',
        'ankit@shopflo.com',
    ];
    return allowed.includes(userEmail);
};

export const SEED_ORG_1_ADMIN_EMAIL = {
    email: 'demo@sortment.com',
    is_primary: true,
};

export const SEED_ORG_1_ADMIN_PASSWORD = {
    password: 'Sortment@2024',
};
export const SEED_ORG_1_ADMIN_ROLE = OrganizationMemberRole.ADMIN;

export const SEED_ORG_1_EDITOR = {
    user_uuid: '80fb8b59-d6b7-4ed6-b969-9849310f3e53',
    first_name: 'Editor',
    last_name: 'User',
    is_marketing_opted_in: true,
    is_tracking_anonymized: false,
    is_setup_complete: true,
    is_active: true,
};
export const SEED_ORG_1_EDITOR_EMAIL = {
    email: 'demo2@lightdash.com',
    is_primary: true,
};
export const SEED_ORG_1_EDITOR_PASSWORD = {
    password: 'demo_password!',
};
export const SEED_ORG_1_EDITOR_ROLE = OrganizationMemberRole.EDITOR;

export const SEED_ORG_1_VIEWER = {
    user_uuid: 'e0dd2003-c291-4e14-b977-7a03b7edc842',
    first_name: 'Viewer',
    last_name: 'User',
    is_marketing_opted_in: true,
    is_tracking_anonymized: false,
    is_setup_complete: true,
    is_active: true,
};
export const SEED_ORG_1_VIEWER_EMAIL = {
    email: 'demo3@lightdash.com',
    is_primary: true,
};
export const SEED_ORG_1_VIEWER_PASSWORD = {
    password: 'demo_password!',
};
export const SEED_ORG_1_VIEWER_ROLE = OrganizationMemberRole.VIEWER;

// Another user
export const SEED_ORG_2 = {
    organization_uuid: '42339eef-359e-4ec4-b810-54ef0b4e3446',
    organization_name: 'Another Shop',
    is_internal: false,
};

export const SEED_ORG_2_ADMIN = {
    user_uuid: '57cd4548-cbe3-42b3-aa13-97821713e307',
    first_name: 'Another',
    last_name: 'User',
    is_marketing_opted_in: true,
    is_tracking_anonymized: false,
    is_setup_complete: true,
    is_active: true,
};

export const SEED_ORG_2_ADMIN_EMAIL = {
    email: 'another@lightdash.com',
    is_primary: true,
};

export const SEED_ORG_2_ADMIN_PASSWORD = {
    password: 'demo_password!',
};
export const SEED_ORG_2_ADMIN_ROLE = OrganizationMemberRole.ADMIN;
export const SEED_EMBED_SECRET = 'zU3h50saDOO20czNFNRok';

export const SEED_PROJECT = {
    project_uuid: '56b6d469-ca96-43a6-905b-f923a59bdbbd',
    name: 'BigQueryXAutomationXDevzery',
    organizationUuid: SEED_ORG_1.organization_uuid,
    fynoWorkspaceId: 'FYAPAF66DF14BIN',
    project_type: ProjectType.DEFAULT,
    dbt_connection_type: DbtProjectType.NONE,
    dbt_connection: null,
    copied_from_project_uuid: null,
};
export const SEED_WRONG_PROJECTID_SORTMENT = {
    project_uuid: '7794f8f6-f4cd-4c2a-bb70-07a30f6dbb85',
    name: 'Jaffle shop',
    project_type: ProjectType.DEFAULT,
    dbt_connection_type: DbtProjectType.DBT,
    dbt_connection: null,
    copied_from_project_uuid: null,
};
export const SEED_CORRECT_RELATION_SORTMENT = {
    uuid: '3aef09d1-85fe-488a-8dda-aa75fea5be38',
    project_uuid: SEED_PROJECT.project_uuid,
    created_by: SEED_ORG_1_ADMIN_EMAIL.email,
    created_at: '2025-04-04 04:59:07.551',
    updated_by: SEED_ORG_1_ADMIN_EMAIL.email,
    updated_at: '2025-04-04 07:16:14.744',
};
export const SEED_AUDIENCES_SORTMENT = {
    id: '9b925de7-eeea-48f6-9c52-77f4c9a0949b',
    project_uuid: SEED_PROJECT.project_uuid,
    relation_id: SEED_CORRECT_RELATION_SORTMENT.uuid,
    name: 'High Value Repeat Customers',
    descreption:
        'Customers with under 50 ID, over $30 lifetime value, 2 orders.',
    status: 'ACTIVE',
    ai_prompt: null,
    generation_strategy: 'AUDIENCE_BUILDER',
    total_count: '2',
    last_run_status: 'SUCCESS',
    user_alias: 'customers_customer_id',
    created_by: SEED_ORG_1_ADMIN.user_uuid,
    updated_by: null,
    created_at: '2025-06-20T12:50:08.600Z',
    updated_at: '2025-06-20T12:50:08.600Z',
    last_run_at: '2025-06-20T12:50:24.146Z',
    run_type: 'MANUAL',
    run_at: null,
    cron: null,
    job_id: '308676',
    audience_last_materialization_name:
        'srt_audience_20250620t125010411z_9b925de7',
    campaign_last_materialization_name: null,
    cron_start_at: null,
    cron_end_at: null,
    preview_config: {
        previewFields: ['customers_email', 'customers_customer_id'],
    },
    scheduler_uuid: null,
};
export const SEED_INCOMPLETE_PROJECTID_SORTMENT = {
    project_uuid: '7794f8f6-',
    name: 'Jaffle shop',
    project_type: ProjectType.DEFAULT,
    dbt_connection_type: DbtProjectType.DBT,
    dbt_connection: null,
    copied_from_project_uuid: null,
    organization_warehouse_credentials_uuid: null,
};
export const SEED_SPACE = {
    name: SEED_PROJECT.name,
};

export const SEED_GROUP = {
    groupUuid: '9d615ede-5758-4954-9fb9-2a07fc415ba5',
    name: 'Org 1 Group',
};

export const TEST_COMMUNICATION_CHANNELS = {
    EMAIL: {
        integrationId: 'I6D8BB21A4C51',
        providerId: 'P3E8CB420A',
        providerName: 'SendGrid',
        projectId: SEED_PROJECT.project_uuid,
        customName: 'SendGridXEmailXCustomerXNotifications',
        fromEmail: 'sortment.test@shopflo.biz',
        fromName: 'Team Sortment Automation',
        replyTo: 'sortment.test@shopflo.biz',
        channelName: 'email',
        channelId: 'C7C600F75D',
        senderProfileId: 'e6ec73ac-260f-45f8-bfd8-a9356a79781d',
    },
    SMS: {
        integrationId: 'IE74B78653496',
        providerId: 'P1ACEACEAS',
        providerName: 'Simulator',
        projectId: SEED_PROJECT.project_uuid,
        customName: 'GupshupXSMSXCustomerXNotifications',
        Sender: 'DEFAULT',
        channelName: 'sms',
        channelId: 'C3E87453FA',
    },
    WHATSAPP: {
        integrationId: 'IFE554BAC7B9C',
        providerId: 'P91C8A9060',
        providerName: 'Meta Facebook',
        projectId: SEED_PROJECT.project_uuid,
        customName: 'MetaXWhatsappXCustomerXNotifications',
        channelName: 'whatsapp',
        channelId: 'C734BF7D19',
    },
    PUSH: {
        integrationId: 'I694F1FA7706C',
        providerId: 'P1ACEACEAP',
        providerName: 'Simulator',
        projectId: SEED_PROJECT.project_uuid,
        customName: 'SimulatorXPUSHXCustomerXNotifications',
        channelName: 'push',
        channelId: 'CA66B06561',
    },
};

export const TEST_TEMPLATES = {
    EMAIL: {
        templateId: 'bacfe73c-0aca-45ec-9daa-e0e251f95233',
        projectId: SEED_PROJECT.project_uuid,
        name: 'Email Announcement LoyaltyBoost',
        description:
            'Announce new loyalty program partnership to boost customer retention.',
        content: {
            html: '<html xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" lang="en"><head><title></title><meta http-equiv="Content-Type" content="text/html; charset=utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><style>\n*{box-sizing:border-box}body{margin:0;padding:0}a[x-apple-data-detectors]{color:inherit!important;text-decoration:inherit!important}#MessageViewBody a{color:inherit;text-decoration:none}p{line-height:inherit}.desktop_hide,.desktop_hide table{mso-hide:all;display:none;max-height:0;overflow:hidden}.image_block img+div{display:none}sub,sup{font-size:75%;line-height:0} @media (max-width:520px){.social_block.desktop_hide .social-table{display:inline-block!important}.mobile_hide{display:none}.row-content{width:100%!important}.stack .column{width:100%;display:block}.mobile_hide{min-height:0;max-height:0;max-width:0;overflow:hidden;font-size:0}.desktop_hide,.desktop_hide table{display:table!important;max-height:none!important}}\n</style></head><body class="body" style="background-color:#fff;margin:0;padding:0;-webkit-text-size-adjust:none;text-size-adjust:none"><span class="preheader" id="preheader-text" style="color: transparent; display: none; height: 0; max-height: 0; max-width: 0; opacity: 0; overflow: hidden; mso-hide: all; visibility: hidden; width: 0;">{{previewText}}</span><table class="nl-container" width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;background-color:#fff"><tbody><tr><td><table class="row row-1" align="center" width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0"><tbody><tr><td><table class="row-content stack" align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;color:#000;width:500px;margin:0 auto" width="500"><tbody><tr><td class="column column-1" width="100%" style="mso-table-lspace:0;mso-table-rspace:0;font-weight:400;text-align:left;border-bottom:0 solid transparent;border-left:0 solid transparent;border-right:0 solid transparent;border-top:0 solid transparent;padding-bottom:5px;padding-top:5px;vertical-align:top"><table class="paragraph_block block-1" width="100%" border="0" cellpadding="10" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;word-break:break-word"><tbody><tr><td class="pad"><div style="color:#000;direction:ltr;font-family:Arial,Helvetica Neue,Helvetica,sans-serif;font-size:14px;font-weight:400;letter-spacing:0;line-height:1.2;text-align:left;mso-line-height-alt:17px"><p id="ij4ac" align="center" style="margin:0;margin-bottom:16px"></p><p id="idhj" style="margin:0;margin-bottom:16px"></p><p id="ipkm" style="margin:0;margin-bottom:16px"></p><p id="i06i" style="margin:0;margin-bottom:16px"></p><p id="iskh" style="margin:0;margin-bottom:16px"></p><p id="idxi" style="margin:0;margin-bottom:16px"></p><p id="iafr" style="margin:0;margin-bottom:16px"></p><p id="i84p" style="margin:0;margin-bottom:16px"></p><p id="i2tj" style="margin:0;margin-bottom:16px"></p><p id="ix15b" style="margin:0;margin-bottom:16px"></p><p id="ik3n4" style="margin:0;margin-bottom:16px">Hi {{name}},</p><p id="ijypi" style="margin:0;margin-bottom:16px">I hope you had a wonderful Diwali and enjoyed the long weekend!</p><p id="i5jk9" style="margin:0;margin-bottom:16px">\nThe festive season always brings a surge in sales, but we know that spike fades as soon as it’s over. That’s why we\'re constantly looking for ways to keep the momentum alive and retain shoppers. To help you do just that, we’re excited to announce our <strong id="ib9pb">partnership with Pop Coins</strong> - a leading loyalty points provider for D2C brands.</p><p style="margin:0;margin-bottom:16px">With this partnership, <strong id="idqex">\nPop Coins users can now seamlessly redeem rewards directly at checkout.</strong></p><p id="ipgdm" style="margin:0;margin-bottom:16px"></p><p style="margin:0"><a id="iinq0r" style="text-decoration: underline; color: #0068A5;"></a></p></div></td></tr></tbody></table><table class="image_block block-2" width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0"><tbody><tr><td class="pad" style="width:100%"><div class="alignment" align="center">\n<div style="max-width:500px"><img src="https://sortment-befree.s3.amazonaws.com/images/n9b1/r0pknz3b/7rm/9kn/kci/2160x2206.png" style="display:block;height:auto;border:0;width:100%" width="500" alt="" title="" height="auto"></div></div></td></tr></tbody></table><table class="paragraph_block block-3" width="100%" border="0" cellpadding="10" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;word-break:break-word"><tbody><tr><td class="pad"><div style="color:#000;direction:ltr;font-family:Arial,Helvetica Neue,Helvetica,sans-serif;font-size:14px;font-weight:400;letter-spacing:0;line-height:1.2;text-align:left;mso-line-height-alt:17px"><p id="ixz3p" style="margin:0;margin-bottom:16px">What this means for {{merchantname}}:</p><p id="i9sbv" style="margin:0;margin-bottom:16px">✨&nbsp;<strong id="ia22w">Higher Conversions:&nbsp;</strong>Shoppers can now redeem their Pop Coins right at checkout for an easier purchase.💰</p><p id="id2s7" style="margin:0;margin-bottom:16px">🤝 <strong id="i980b">Enhanced Loyalty:</strong> Customers can see how many Pop Coins they’ll earn per order, giving them more reasons to return.🛍️</p><p style="margin:0;margin-bottom:16px">With Shopflo + Pop Coins, your checkout just got even more powerful! 🚀</p><p style="margin:0;margin-bottom:16px">&nbsp;</p><p id="iwd3y" style="margin:0">If you\'re ready to kick off your rewards journey, simply type ‘<strong id="i8dh4z">\nPOP Coins</strong>’ and send it to <a id="ie4jeg" href="https://staging.sortment.com/projects/0c47c60d-244d-4dfe-a48e-66b2d5a75bfc/templates/email/420c3c02-182f-4f5e-beaf-6148cb1cf5e3" style="text-decoration: underline; color: #0068A5;" data-srt-id="url_1">support@shopflo.com</a>.</p></div></td></tr></tbody></table><table class="divider_block block-4" width="100%" border="0" cellpadding="10" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0"><tbody><tr><td class="pad"><div class="alignment" align="center"><table border="0" cellpadding="0" cellspacing="0" role="presentation" width="100%" style="mso-table-lspace:0;mso-table-rspace:0"><tbody><tr><td class="divider_inner" style="font-size:1px;line-height:1px;border-top:1px solid #bbb"><span style="word-break: break-word;">&#8202;</span></td></tr></tbody></table></div></td></tr></tbody></table><table class="paragraph_block block-5" width="100%" border="0" cellpadding="10" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;word-break:break-word"><tbody><tr><td class="pad"><div style="color:#000;direction:ltr;font-family:Arial,Helvetica Neue,Helvetica,sans-serif;font-size:14px;font-weight:400;letter-spacing:0;line-height:1.2;text-align:center;mso-line-height-alt:17px"><p id="ipsxar" style="margin:0;margin-bottom:16px"><em id="iics85">We’re thrilled about this partnership & can’t wait for {{merchantname}} to try it out.<br id="i0sdkh">Here’s to making your Shopflo experience even better!</em></p><p id="ijm0a3" style="margin:0;margin-bottom:16px"></p><p id="izxwmo" style="margin:0">💙 Team Shopflo&nbsp;</p></div></td></tr></tbody></table><table class="social_block block-6" width="100%" border="0" cellpadding="10" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0"><tbody><tr><td class="pad"><div class="alignment" align="center"><table class="social-table" width="108px" border="0" cellpadding="0" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;display:inline-block"><tbody><tr><td style="padding:0 2px 0 2px">\n<a href="https://twitter.com/" target="_blank" data-srt-id="url_2"><img src="https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-color/twitter@2x.png" width="32" height="auto" alt="X" title="X" style="display:block;height:auto;border:0"></a></td><td style="padding:0 2px 0 2px"><a href="https://instagram.com/" target="_blank" data-srt-id="url_3"><img src="https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-color/instagram@2x.png" width="32" height="auto" alt="Instagram" title="Instagram" style="display:block;height:auto;border:0"></a></td><td style="padding:0 2px 0 2px"><a href="https://www.linkedin.com/" target="_blank" data-srt-id="url_4"><img src="https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-color/linkedin@2x.png" width="32" height="auto" alt="LinkedIn" title="LinkedIn" style="display:block;height:auto;border:0"></a></td></tr></tbody></table></div></td></tr></tbody></table><table class="text_block block-7" width="100%" border="0" cellpadding="10" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;word-break:break-word"><tbody><tr><td class="pad"><div style="font-family:sans-serif"><div class="" style="font-size:14px;font-family:Arial,Helvetica Neue,Helvetica,sans-serif;mso-line-height-alt:16.8px;color:#555;line-height:1.2"><p style="margin:0;font-size:14px;text-align:center;mso-line-height-alt:16.8px"><span style="word-break: break-word; font-size: 9px;">If abandoned carts & slow checkouts don’t faze you, click below to&nbsp;Unsubscribe</span></p></div></div></td></tr></tbody></table></td></tr></tbody></table></td></tr></tbody></table><table class="row row-2" align="center" width="100%" border="0" cellpadding="0" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0"><tbody><tr><td><table class="row-content stack" align="center" border="0" cellpadding="0" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;border-radius:0;color:#000;width:500px;margin:0 auto" width="500"><tbody><tr><td class="column column-1" width="50%" style="mso-table-lspace:0;mso-table-rspace:0;font-weight:400;text-align:left;padding-bottom:5px;padding-top:5px;vertical-align:top"><table class="text_block block-1" width="100%" border="0" cellpadding="10" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;word-break:break-word"><tbody><tr><td class="pad"><div style="font-family:sans-serif"><div class="" style="font-size:14px;font-family:Arial,Helvetica Neue,Helvetica,sans-serif;mso-line-height-alt:16.8px;color:#555;line-height:1.2"><p style="margin:0;font-size:14px;text-align:right;mso-line-height-alt:16.8px"><a href="{{site_url}}/unsubscribe?campaign={{campaign_id}}&channel={{campaign_channel}}&projectId={{project_id}}&subGroupId={{subscription_group_id}}&userId={{distinct_id}}" target="_blank" rel="noopener" style="color: #0068A5;">Unsubscribe</a>&nbsp;</p></div></div></td></tr></tbody></table></td><td class="column column-2" width="50%" style="mso-table-lspace:0;mso-table-rspace:0;font-weight:400;text-align:left;padding-bottom:5px;padding-top:5px;vertical-align:top"><table class="text_block block-1" width="100%" border="0" cellpadding="10" cellspacing="0" role="presentation" style="mso-table-lspace:0;mso-table-rspace:0;word-break:break-word"><tbody><tr><td class="pad"><div style="font-family:sans-serif"><div class="" style="font-size:14px;font-family:Arial,Helvetica Neue,Helvetica,sans-serif;mso-line-height-alt:16.8px;color:#555;line-height:1.2"><p style="margin:0;font-size:14px;mso-line-height-alt:16.8px"><a href="{{site_url}}/subscription-preferences/channel?campaign={{campaign_id}}&channel={{campaign_channel}}&projectId={{project_id}}&subGroupId={{subscription_group_id}}&userId={{distinct_id}}" target="_blank" rel="noopener" style="color: #0068A5;">Unsubscribe Preferences</a>&nbsp;</p></div></div></td></tr></tbody></table></td></tr></tbody></table></td></tr></tbody></table></td></tr></tbody></table></body></html>',
            json: {
                page: {
                    body: {
                        content: {
                            style: {
                                color: '#000000',
                                'font-family':
                                    'Arial, Helvetica Neue, Helvetica, sans-serif',
                            },
                            computedStyle: {
                                linkColor: '#0068A5',
                                messageWidth: '500px',
                                messageBackgroundColor: 'transparent',
                            },
                        },
                        webFonts: [],
                        container: {
                            style: {
                                'background-color': '#FFFFFF',
                            },
                        },
                    },
                    rows: [
                        {
                            type: 'one-column-empty',
                            uuid: 'c738fb29-e88f-4b24-bd0c-deb9a414ff5e',
                            empty: false,
                            locked: false,
                            synced: false,
                            columns: [
                                {
                                    uuid: '1136212c-fb6b-4d18-90dc-43731c926110',
                                    style: {
                                        'border-top': '0 solid transparent',
                                        'border-left': '0 solid transparent',
                                        'padding-top': '5px',
                                        'border-right': '0 solid transparent',
                                        'padding-left': '0px',
                                        'border-bottom': '0 solid transparent',
                                        'padding-right': '0px',
                                        'padding-bottom': '5px',
                                        'background-color': 'transparent',
                                    },
                                    modules: [
                                        {
                                            type: 'mailup-bee-newsletter-modules-paragraph',
                                            uuid: '8f7e773b-9a24-49ad-8138-44c662e4cc0d',
                                            locked: false,
                                            descriptor: {
                                                style: {
                                                    'padding-top': '10px',
                                                    'padding-left': '10px',
                                                    'padding-right': '10px',
                                                    'padding-bottom': '10px',
                                                },
                                                paragraph: {
                                                    html: '<p id="ij4ac" align="center"></p>\n<p id="idhj"></p>\n<p id="ipkm"></p>\n<p id="i06i"></p>\n<p id="iskh"></p>\n<p id="idxi"></p>\n<p id="iafr"></p>\n<p id="i84p"></p>\n<p id="i2tj"></p>\n<p id="ix15b"></p>\n<p id="ik3n4">Hi {{name}},</p>\n<p id="ijypi">I hope you had a wonderful Diwali and enjoyed the long weekend!</p>\n<p id="i5jk9">The festive season always brings a surge in sales, but we know that spike fades as soon as it’s over. That’s why we\'re constantly looking for ways to keep the momentum alive and retain shoppers. To help you do just that, we’re excited to announce our <strong id="ib9pb">partnership with Pop Coins</strong> - a leading loyalty points provider for D2C brands.</p>\n<p>With this partnership, <strong id="idqex">Pop Coins users can now seamlessly redeem rewards directly at checkout.</strong></p>\n<p id="ipgdm"></p>\n<p><a id="iinq0r" style="text-decoration: underline;"></a></p>',
                                                    style: {
                                                        color: '#000000',
                                                        direction: 'ltr',
                                                        'font-size': '14px',
                                                        'text-align': 'left',
                                                        'font-family':
                                                            'inherit',
                                                        'font-weight': '400',
                                                        'line-height': '120%',
                                                        'letter-spacing': '0px',
                                                    },
                                                    computedStyle: {
                                                        linkColor: '#0068A5',
                                                        paragraphSpacing:
                                                            '16px',
                                                    },
                                                },
                                                mobileStyle: {},
                                                computedStyle: {
                                                    hideContentOnAmp: false,
                                                    hideContentOnHtml: false,
                                                    hideContentOnMobile: false,
                                                    hideContentOnDesktop: false,
                                                },
                                            },
                                        },
                                        {
                                            type: 'mailup-bee-newsletter-modules-image',
                                            uuid: 'ee8efe2e-400f-4f3a-9966-98179dba923e',
                                            locked: false,
                                            descriptor: {
                                                image: {
                                                    alt: '',
                                                    src: 'https://sortment-befree.s3.amazonaws.com/images/n9b1/r0pknz3b/7rm/9kn/kci/2160x2206.png',
                                                    href: '',
                                                    width: '1920px',
                                                    height: '1960px',
                                                    target: '_blank',
                                                },
                                                style: {
                                                    width: '100%',
                                                    'padding-top': '0px',
                                                    'padding-left': '0px',
                                                    'border-radius': '0px',
                                                    'padding-right': '0px',
                                                    'padding-bottom': '0px',
                                                },
                                                mobileStyle: {},
                                                computedStyle: {
                                                    class: 'center autowidth',
                                                    width: '500px',
                                                    hideContentOnMobile: false,
                                                },
                                            },
                                        },
                                        {
                                            type: 'mailup-bee-newsletter-modules-paragraph',
                                            uuid: '39b63c5f-253a-4c4f-acc0-1d1a5b3b84df',
                                            locked: false,
                                            descriptor: {
                                                style: {
                                                    'padding-top': '10px',
                                                    'padding-left': '10px',
                                                    'padding-right': '10px',
                                                    'padding-bottom': '10px',
                                                },
                                                paragraph: {
                                                    html: '<p id="ixz3p">What this means for {{merchantname}}:</p>\n<p id="i9sbv">✨&nbsp;<strong id="ia22w">Higher Conversions:&nbsp;</strong>Shoppers can now redeem their Pop Coins right at checkout for an easier purchase.💰</p>\n<p id="id2s7">🤝 <strong id="i980b">Enhanced Loyalty:</strong> Customers can see how many Pop Coins they’ll earn per order, giving them more reasons to return.🛍️</p>\n<p>With Shopflo + Pop Coins, your checkout just got even more powerful! 🚀</p>\n<p>&nbsp;</p>\n<p id="iwd3y">If you\'re ready to kick off your rewards journey, simply type ‘<strong id="i8dh4z">POP Coins</strong>’ and send it to <a id="ie4jeg" href="https://staging.sortment.com/projects/0c47c60d-244d-4dfe-a48e-66b2d5a75bfc/templates/email/420c3c02-182f-4f5e-beaf-6148cb1cf5e3" style="text-decoration: underline;">support@shopflo.com</a>.</p>',
                                                    style: {
                                                        color: '#000000',
                                                        direction: 'ltr',
                                                        'font-size': '14px',
                                                        'text-align': 'left',
                                                        'font-family':
                                                            'inherit',
                                                        'font-weight': '400',
                                                        'line-height': '120%',
                                                        'letter-spacing': '0px',
                                                    },
                                                    computedStyle: {
                                                        linkColor: '#0068A5',
                                                        paragraphSpacing:
                                                            '16px',
                                                    },
                                                },
                                                mobileStyle: {},
                                                computedStyle: {
                                                    hideContentOnAmp: false,
                                                    hideContentOnHtml: false,
                                                    hideContentOnMobile: false,
                                                    hideContentOnDesktop: false,
                                                },
                                            },
                                        },
                                        {
                                            type: 'mailup-bee-newsletter-modules-divider',
                                            uuid: '565fa910-6bcc-4717-a50d-92876b4077f1',
                                            locked: false,
                                            descriptor: {
                                                style: {
                                                    'padding-top': '10px',
                                                    'padding-left': '10px',
                                                    'padding-right': '10px',
                                                    'padding-bottom': '10px',
                                                },
                                                divider: {
                                                    style: {
                                                        width: '100%',
                                                        'border-top':
                                                            '1px solid #BBBBBB',
                                                    },
                                                },
                                                mobileStyle: {},
                                                computedStyle: {
                                                    align: 'center',
                                                    hideContentOnMobile: false,
                                                },
                                            },
                                        },
                                        {
                                            type: 'mailup-bee-newsletter-modules-paragraph',
                                            uuid: '2c663a84-46de-48af-8639-f2474f719960',
                                            locked: false,
                                            descriptor: {
                                                style: {
                                                    'padding-top': '10px',
                                                    'padding-left': '10px',
                                                    'padding-right': '10px',
                                                    'padding-bottom': '10px',
                                                },
                                                paragraph: {
                                                    html: '<p id="ipsxar"><em id="iics85">We’re thrilled about this partnership &amp; can’t wait for {{merchantname}} to try it out.<br id="i0sdkh">Here’s to making your Shopflo experience even better!</em></p>\n<p id="ijm0a3"></p>\n<p id="izxwmo">💙 Team Shopflo&nbsp;</p>',
                                                    style: {
                                                        color: '#000000',
                                                        direction: 'ltr',
                                                        'font-size': '14px',
                                                        'text-align': 'center',
                                                        'font-family':
                                                            'inherit',
                                                        'font-weight': '400',
                                                        'line-height': '120%',
                                                        'letter-spacing': '0px',
                                                    },
                                                    computedStyle: {
                                                        linkColor: '#0068A5',
                                                        paragraphSpacing:
                                                            '16px',
                                                    },
                                                },
                                                mobileStyle: {},
                                                computedStyle: {
                                                    hideContentOnAmp: false,
                                                    hideContentOnHtml: false,
                                                    hideContentOnMobile: false,
                                                    hideContentOnDesktop: false,
                                                },
                                            },
                                        },
                                        {
                                            type: 'mailup-bee-newsletter-modules-social',
                                            uuid: 'ed9d9836-9231-4e40-8756-891c6a296776',
                                            locked: false,
                                            descriptor: {
                                                style: {
                                                    'text-align': 'center',
                                                    'padding-top': '10px',
                                                    'padding-left': '10px',
                                                    'padding-right': '10px',
                                                    'padding-bottom': '10px',
                                                },
                                                iconsList: {
                                                    icons: [
                                                        {
                                                            id: 'x',
                                                            name: 'X',
                                                            text: '',
                                                            type: 'follow',
                                                            image: {
                                                                alt: 'X',
                                                                src: 'https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-color/twitter@2x.png',
                                                                href: 'https://twitter.com/',
                                                                title: 'X',
                                                                prefix: 'https://twitter.com/',
                                                                target: '_blank',
                                                            },
                                                        },
                                                        {
                                                            id: 'instagram',
                                                            name: 'Instagram',
                                                            text: '',
                                                            type: 'follow',
                                                            image: {
                                                                alt: 'Instagram',
                                                                src: 'https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-color/instagram@2x.png',
                                                                href: 'https://instagram.com/',
                                                                title: 'Instagram',
                                                                prefix: 'https://instagram.com/',
                                                                target: '_blank',
                                                            },
                                                        },
                                                        {
                                                            id: 'linkedin',
                                                            name: 'LinkedIn',
                                                            text: '',
                                                            type: 'follo',
                                                            image: {
                                                                alt: 'LinkedIn',
                                                                src: 'https://app-rsrc.getbee.io/public/resources/social-networks-icon-sets/circle-color/linkedin@2x.png',
                                                                href: 'https://www.linkedin.com/',
                                                                title: 'LinkedIn',
                                                                prefix: 'https://www.linkedin.com/',
                                                                target: '_blank',
                                                            },
                                                        },
                                                    ],
                                                },
                                                mobileStyle: {},
                                                computedStyle: {
                                                    width: 151,
                                                    height: 57,
                                                    padding: '0 2.5px 0 2.5px',
                                                    iconsDefaultWidth: 32,
                                                    hideContentOnMobile: false,
                                                },
                                            },
                                        },
                                        {
                                            type: 'mailup-bee-newsletter-modules-text',
                                            uuid: 'f7387ed2-ffa5-4940-b9e4-e47779c123cc',
                                            locked: false,
                                            descriptor: {
                                                text: {
                                                    html: '<div class="txtTinyMce-wrapper" style="font-size: 14px; line-height: 16px;" data-mce-style="font-size: 14px; line-height: 16px;"><p style="font-size: 14px; line-height: 16px; word-break: break-word; text-align: center;" data-mce-style="font-size: 14px; line-height: 16px; word-break: break-word; text-align: center;"><span style="font-size: 9px; line-height: 10px;" data-mce-style="font-size: 9px; line-height: 10px;">If abandoned carts &amp; slow checkouts don’t faze you, click below to&nbsp;Unsubscribe</span></p></div>',
                                                    style: {
                                                        color: '#555555',
                                                        'font-family':
                                                            'inherit',
                                                        'line-height': '120%',
                                                    },
                                                    computedStyle: {
                                                        linkColor: '#0068A5',
                                                    },
                                                },
                                                style: {
                                                    'padding-top': '10px',
                                                    'padding-left': '10px',
                                                    'padding-right': '10px',
                                                    'padding-bottom': '10px',
                                                },
                                                mobileStyle: {},
                                                computedStyle: {
                                                    hideContentOnMobile: false,
                                                },
                                            },
                                        },
                                    ],
                                    'grid-columns': 12,
                                },
                            ],
                            content: {
                                style: {
                                    color: '#000000',
                                    width: '500px',
                                    'background-color': 'transparent',
                                    'background-image': 'none',
                                    'background-repeat': 'no-repeat',
                                    'background-position': 'top left',
                                },
                                computedStyle: {
                                    verticalAlign: 'top',
                                    hideContentOnMobile: false,
                                    rowColStackOnMobile: true,
                                    hideContentOnDesktop: false,
                                    rowReverseColStackOnMobile: false,
                                },
                            },
                            container: {
                                style: {
                                    'background-color': 'transparent',
                                    'background-image': 'none',
                                    'background-repeat': 'no-repeat',
                                    'background-position': 'top left',
                                },
                            },
                        },
                        {
                            type: 'Unsubscribe',
                            uuid: 'd4f80f01-b613-4d11-892f-e7abba8eb9c0',
                            empty: false,
                            locked: false,
                            synced: false,
                            columns: [
                                {
                                    uuid: 'c98ff138-e7b7-49e0-ad76-85e91b8d5a7c',
                                    style: {
                                        'border-top': '0px solid transparent',
                                        'border-left': '0px solid transparent',
                                        'padding-top': '5px',
                                        'border-right': '0px solid transparent',
                                        'padding-left': '0px',
                                        'border-bottom':
                                            '0px solid transparent',
                                        'padding-right': '0px',
                                        'padding-bottom': '5px',
                                        'background-color': 'transparent',
                                    },
                                    modules: [
                                        {
                                            type: 'mailup-bee-newsletter-modules-text',
                                            uuid: 'd1ca5ff7-6af7-4cdc-b3e8-6175d31c3419',
                                            align: 'left',
                                            locked: false,
                                            descriptor: {
                                                text: {
                                                    html: '<div class="txtTinyMce-wrapper" style="font-size:14px;line-height:16px;font-family:inherit;" data-mce-style="font-size:14px;line-height:16px;font-family:inherit;"><p style="font-size:14px;line-height:16px;word-break:break-word;text-align:right;" data-mce-style="font-size:14px;line-height:16px;word-break:break-word;text-align:right;"><code data-bee-type="speciallink" data-bee-code=""><a href="{{site_url}}/unsubscribe?campaign={{campaign_id}}&amp;channel={{campaign_channel}}&amp;projectId={{project_id}}&amp;subGroupId={{subscription_group_id}}&amp;userId={{distinct_id}}" target="_blank" rel="noopener">Unsubscribe</a></code>&nbsp;</p></div>',
                                                    style: {
                                                        color: '#555555',
                                                        'font-family':
                                                            'inherit',
                                                        'line-height': '120%',
                                                    },
                                                    computedStyle: {
                                                        linkColor: '#0068A5',
                                                    },
                                                },
                                                style: {
                                                    'padding-top': '10px',
                                                    'padding-left': '10px',
                                                    'padding-right': '10px',
                                                    'padding-bottom': '10px',
                                                },
                                                computedStyle: {
                                                    hideContentOnMobile: false,
                                                },
                                            },
                                        },
                                    ],
                                    'grid-columns': 6,
                                },
                                {
                                    uuid: '689da66b-add9-43d5-9731-da8a941e35e8',
                                    style: {
                                        'border-top': '0px solid transparent',
                                        'border-left': '0px solid transparent',
                                        'padding-top': '5px',
                                        'border-right': '0px solid transparent',
                                        'padding-left': '0px',
                                        'border-bottom':
                                            '0px solid transparent',
                                        'padding-right': '0px',
                                        'padding-bottom': '5px',
                                        'background-color': 'transparent',
                                    },
                                    modules: [
                                        {
                                            type: 'mailup-bee-newsletter-modules-text',
                                            uuid: '2f14f66b-c8bd-426f-8602-df134d425128',
                                            align: 'left',
                                            locked: false,
                                            descriptor: {
                                                text: {
                                                    html: '<div class="txtTinyMce-wrapper" style="font-size:14px;line-height:16px;font-family:inherit;" data-mce-style="font-size:14px;line-height:16px;font-family:inherit;"><p style="font-size:14px;line-height:16px;word-break:break-word;" data-mce-style="font-size:14px;line-height:16px;word-break:break-word;"><code data-bee-type="speciallink" data-bee-code=""><a href="{{site_url}}/subscription-preferences/channel?campaign={{campaign_id}}&amp;channel={{campaign_channel}}&amp;projectId={{project_id}}&amp;subGroupId={{subscription_group_id}}&amp;userId={{distinct_id}}" target="_blank" rel="noopener">Unsubscribe Preferences</a></code>&nbsp;</p></div>',
                                                    style: {
                                                        color: '#555555',
                                                        'font-family':
                                                            'inherit',
                                                        'line-height': '120%',
                                                    },
                                                    computedStyle: {
                                                        linkColor: '#0068A5',
                                                    },
                                                },
                                                style: {
                                                    'padding-top': '10px',
                                                    'padding-left': '10px',
                                                    'padding-right': '10px',
                                                    'padding-bottom': '10px',
                                                },
                                                computedStyle: {
                                                    hideContentOnMobile: false,
                                                },
                                            },
                                        },
                                    ],
                                    'grid-columns': 6,
                                },
                            ],
                            content: {
                                style: {
                                    color: '#000000',
                                    width: '500px',
                                    'border-top': '0px solid transparent',
                                    'border-left': '0px solid transparent',
                                    'border-right': '0px solid transparent',
                                    'border-bottom': '0px solid transparent',
                                    'border-radius': '0px',
                                    'background-color': 'transparent',
                                    'background-image': 'none',
                                    'background-repeat': 'no-repeat',
                                    'background-position': 'top left',
                                },
                                computedStyle: {
                                    verticalAlign: 'top',
                                    hideContentOnMobile: false,
                                    rowColStackOnMobile: true,
                                    hideContentOnDesktop: false,
                                    rowReverseColStackOnMobile: false,
                                },
                            },
                            metadata: {
                                name: 'Unsubscribe',
                                categoryId: 142,
                                rowIdentifier:
                                    'ed1f4f6e-f427-40e9-a8c2-ff5914c5fb5b',
                            },
                            container: {
                                style: {
                                    'background-color': 'transparent',
                                    'background-image': 'none',
                                    'background-repeat': 'no-repeat',
                                    'background-position': 'top left',
                                },
                            },
                        },
                    ],
                    title: '',
                    template: {
                        version: '2.0.0',
                    },
                    description: '',
                },
                comments: {},
            },
            subject: '',
            variables: {
                body: ['name', 'merchantname'],
                subject: [],
            },
            parsedVariables: [
                {
                    key: 'url_1',
                    type: 'string',
                    value: 'https://staging.sortment.com/projects/0c47c60d-244d-4dfe-a48e-66b2d5a75bfc/templates/email/420c3c02-182f-4f5e-beaf-6148cb1cf5e3',
                },
                {
                    key: 'url_2',
                    type: 'string',
                    value: 'https://twitter.com/',
                },
                {
                    key: 'url_3',
                    type: 'string',
                    value: 'https://instagram.com/',
                },
                {
                    key: 'url_4',
                    type: 'string',
                    value: 'https://www.linkedin.com/',
                },
            ],
        },
    },
    SMS: {
        templateId: '3ff5bbbd-e341-4456-ab17-9615385a8f1b',
        projectId: SEED_PROJECT.project_uuid,
        name: 'OTP_SHOPFLO_DEVZERY',
        description: 'SMS Template for automation testing',
        content: {
            body: 'Your One Time Password (OTP) to verify your order with {{brand}} is {{otp}}. Do not share your OTP with anyone.\n\n\n\nTeam Shopflo',
            dltTemplateId: '1007182183463678448',
            flash: false,
            unicode: false,
        },
        variables: {
            body: ['brand', 'otp'],
        },
        contentMappings: {
            body: {
                otp: {
                    type: 'static',
                    value: '',
                    defaultValue: '1234',
                },
                brand: {
                    type: 'static',
                    value: '',
                    defaultValue: 'Shpflo',
                },
            },
            header: {},
            buttons: {},
            subject: {},
            preheader: {},
        },
    },
    WHATSAPP: {
        templateId: '1012311131062885',
        projectId: SEED_PROJECT.project_uuid,
        name: 'dynamic_btn',
        description: '',
        content: {
            header: {
                type: 'text',
                text: '',
            },
            body: {
                text: 'Hello dynamic btn',
            },
            footer: {
                text: '',
            },
            buttons: [
                {
                    type: 'url',
                    title: 'Visit website',
                    id: 'Visit website',
                    domain: 'https://www.google.com/{{1}}',
                    isDynamic: 'true',
                    sample: 'https://www.google.com/{{1}}',
                },
            ],
        },
    },
    PUSH: {
        templateId: '81130291-c8b8-4253-822f-712b63396d30',
        projectId: SEED_PROJECT.project_uuid,
        name: 'Order Shipped - Scenario 1',
        description: 'Push Template for automation testing',
        content: {
            title: 'Your order is on the way! 🚚',
            message:
                'Hey {{firstName}}, your order #{{orderId}} has been shipped and is expected to arrive by {{deliveryDate}}. Tap to track your package!',
            deliveryPriority: 'high',
        },
        variables: {
            message: ['firstName', 'orderId', 'deliveryDate'],
        },
        contentMappings: {
            message: {
                firstName: {
                    type: 'dimension',
                    value: 'customers_first_name',
                    defaultValue: 'firstName',
                },
                orderId: {
                    type: 'dimension',
                    value: 'orders_order_id',
                    defaultValue: 'OrderID',
                },
                deliveryDate: {
                    type: 'dimension',
                    value: 'customers_most_recent_order',
                    defaultValue: 'Recently',
                },
            },
            title: {},
            image: {},
        },
    },
};

export const TEST_SUBSCRIPTION_GROUPS = {
    EMAIL: {
        subscriptionGroupId: '1f6ac370-362c-421e-aedf-fc7a969221c4',
        name: 'Delivery updates',
        description:
            'Emails informing users about status changes to their order fulfilment.',
        subscriptionCategoryId: '8f50d912-bb53-48f6-a8c3-04ddaf0fb5ea',
        categoryName: 'Optout',
        type: 'REQUIRED',
        projectId: SEED_PROJECT.project_uuid,
        channel: 'email',
    },
    SMS: {
        subscriptionGroupId: '504d00f4-f68e-4c74-b693-7e14a2015878',
        name: 'SMS Updates & Alerts',
        description:
            'Time-sensitive notifications, order updates, and promotional offers delivered via text message to subscribed customers.',
        subscriptionCategoryId: '4d772934-450a-4447-932d-8a234d979134',
        categoryName: 'Optout',
        type: 'REQUIRED',
        projectId: SEED_PROJECT.project_uuid,
        channel: 'sms',
    },
    WHATSAPP: {
        subscriptionGroupId: '37df0d26-6fc6-4793-be30-7a3b855df5fb',
        name: 'WhatsApp Business Communications',
        description:
            'Interactive customer engagement channel for order updates, support queries, and exclusive offers through WhatsApp messaging.',
        subscriptionCategoryId: 'ec2fd0d4-86fa-49d1-b824-53f11bb1a103',
        categoryName: 'Optin',
        type: 'OPT_IN',
        projectId: SEED_PROJECT.project_uuid,
        channel: 'whatsapp',
    },
};

export const TEST_JOURNEY_EVENTS = {
    eventId: '6c2e983d-b3e6-47f0-9124-1b05acee21ee',
    projectId: SEED_PROJECT.project_uuid,
    eventSource: 'Devzery Shopflo Event',
    eventSourceId: '4761d5bc-d2b4-483c-af36-85f8173c1b8a',
    authorizationkey: 'Bearer srt_b702389e1c30e222728bc76bbbd60c5cd5f39a93',
    eventNameKeys: '$.event_name',
    columnLabel: [
        'Event Name',
        'Channel',
        'Merchant Id',
        'Firstname',
        'Lastname',
        'Phone',
    ],
    eventName: 'abandoned_checkouts',
    label: 'abandoned checkouts',
    mapperSchema: {
        srt_client_event_id: {
            required: true,
            mapperType: 'VALUE',
            mapperConfig: {
                path: '$.event_id',
                type: 'VALUE',
            },
            fieldConfig: null,
            dataType: 'string',
            isUrlEncoded: false,
        },
        srt_client_timestamp: {
            required: true,
            mapperType: 'VALUE',
            mapperConfig: {
                path: '$.timestamp',
                type: 'VALUE',
            },
            fieldConfig: null,
            dataType: 'number',
            isUrlEncoded: false,
        },
        srt_user_id: {
            required: true,
            mapperType: 'VALUE',
            mapperConfig: {
                path: '$.user.email_id',
                type: 'VALUE',
            },
            fieldConfig: null,
            dataType: 'string',
            isUrlEncoded: false,
        },
        srt_event_name: {
            required: true,
            mapperType: 'VALUE',
            mapperConfig: {
                path: '$.event_name',
                type: 'VALUE',
            },
            fieldConfig: null,
            dataType: 'string',
            isUrlEncoded: false,
        },
        merchant_id: {
            required: true,
            mapperType: 'VALUE',
            mapperConfig: {
                path: '$.user.merchant_id',
                type: 'VALUE',
            },
            fieldConfig: null,
            dataType: 'string',
            isUrlEncoded: false,
        },
        channel: {
            required: true,
            mapperType: 'VALUE',
            mapperConfig: {
                path: '$.user.channel',
                type: 'VALUE',
            },
            fieldConfig: null,
            dataType: 'string',
            isUrlEncoded: false,
        },
        phone: {
            required: true,
            mapperType: 'VALUE',
            mapperConfig: {
                path: '$.user.phone_number',
                type: 'VALUE',
            },
            fieldConfig: null,
            dataType: 'string',
            isUrlEncoded: false,
        },
        firstname: {
            required: true,
            mapperType: 'VALUE',
            mapperConfig: {
                path: '$.user.first_name',
                type: 'VALUE',
            },
            fieldConfig: null,
            dataType: 'string',
            isUrlEncoded: false,
        },
        lastname: {
            required: true,
            mapperType: 'VALUE',
            mapperConfig: {
                path: '$.user.last_name',
                type: 'VALUE',
            },
            fieldConfig: null,
            dataType: 'string',
            isUrlEncoded: false,
        },
    },
    sampleEventPayload: (channel = 'email') => ({
        event_id: uuidv4(),
        event_name: 'abandoned_checkouts',
        timestamp: Date.now(),
        user: {
            email_id: 'devzery.clients@gmail.com',
            first_name: 'Devzry',
            last_name: 'Shpflo',
            channel,
            phone_number: '+919444444444',
            merchant_id: uuidv4(),
        },
    }),
};

export const TEST_CAMPAIGN_AUDIENCES = {
    WAREHOUSE: {
        audienceId: '9b925de7-eeea-48f6-9c52-77f4c9a0949b',
        audienceType: 'WAREHOUSE',
        audienceName: 'High Value Repeat Customers',
        description:
            'Customers with under 50 ID, over $30 lifetime value, 2 orders.',
        projectId: SEED_PROJECT.project_uuid,
        relationId: SEED_CORRECT_RELATION_SORTMENT.uuid,
    },
};

export const SEED_GROUP_2 = {
    groupUuid: '1456c265-f375-4d64-bd33-105c84ad9b5d',
    name: 'Org 1 Editor Group',
};

export type ArgumentsOf<F extends Function> = F extends (
    ...args: infer A
) => AnyType
    ? A
    : never;

export const getVisibleFields = (explore: Explore): CompiledField[] =>
    getFields(explore).filter(({ hidden }) => !hidden);

export const findFieldByIdInExplore = (
    explore: Explore,
    id: FieldId,
): Field | undefined =>
    getFields(explore).find((field) => getItemId(field) === id);

export const snakeCaseName = (text: string): string =>
    text
        .replace(/\W+/g, ' ')
        .split(/ |\B(?=[A-Z])/)
        .map((word) => word.toLowerCase())
        .join('_');

export const hasSpecialCharacters = (text: string) => /[^a-zA-Z ]/g.test(text);

export type CacheMetadata = {
    cacheUpdatedTime?: Date;
    cacheExpiresAt?: Date;
    cacheKey?: string;
    cacheHit: boolean;
};

export type ApiQueryResults = {
    metricQuery: MetricQuery;
    cacheMetadata: CacheMetadata;
    rows: ResultRow[];
    fields: ItemsMap;
};

type ApiExecuteAsyncQueryResultsCommon = {
    queryUuid: string;
    cacheMetadata: CacheMetadata;
    parameterReferences: string[]; // params needed for query to run
    usedParametersValues: ParametersValuesMap; // params values used
};

export type ApiExecuteAsyncMetricQueryResults =
    ApiExecuteAsyncQueryResultsCommon & {
        metricQuery: MetricQuery;
        fields: ItemsMap;
        warnings: QueryWarning[];
    };

export type ApiExecuteAsyncDashboardChartQueryResults =
    ApiExecuteAsyncQueryResultsCommon & {
        metricQuery: MetricQuery;
        fields: ItemsMap;
        appliedDashboardFilters: DashboardFilters;
    };

export type ApiExecuteAsyncSqlQueryResults =
    ApiExecuteAsyncQueryResultsCommon & {
        // leaving empty for now
    };

export type ApiExecuteAsyncDashboardSqlChartQueryResults =
    ApiExecuteAsyncQueryResultsCommon & {
        appliedDashboardFilters: DashboardFilters;
    };

export type ReadyQueryResultsPage = ResultsPaginationMetadata<ResultRow> & {
    queryUuid: string;
    columns: ResultColumns;
    rows: ResultRow[];
    initialQueryExecutionMs: number;
    resultsPageExecutionMs: number;
    status: QueryHistoryStatus.READY;
    pivotDetails: {
        // Unlimited total column count, this is used to display a warning to the user in the frontend when the number of columns is over MAX_PIVOT_COLUMN_LIMIT
        totalColumnCount: number | null;
        indexColumn: PivotConfiguration['indexColumn'] | undefined;
        valuesColumns: PivotValuesColumn[];
        groupByColumns: GroupByColumn[] | undefined;
        sortBy: SortBy | undefined;
        originalColumns: ResultColumns;
    } | null;
};

export type ApiGetAsyncQueryResults =
    | ReadyQueryResultsPage
    | {
          status: QueryHistoryStatus.PENDING | QueryHistoryStatus.CANCELLED;
          queryUuid: string;
      }
    | {
          status: QueryHistoryStatus.ERROR;
          queryUuid: string;
          error: string | null;
      };

export type ApiDownloadAsyncQueryResults = {
    fileUrl: string;
};

export type ApiDownloadAsyncQueryResultsAsCsv = {
    fileUrl: string;
    truncated: boolean;
};

export type ApiDownloadAsyncQueryResultsAsXlsx = {
    fileUrl: string;
    truncated: boolean;
};

export type ApiChartAndResults = {
    chart: SavedChart;
    explore: Explore;
    appliedDashboardFilters: DashboardFilters | undefined;
    metricQuery: MetricQuery;
    cacheMetadata: CacheMetadata;
    rows: ResultRow[];
    fields: ItemsMap;
};

export type ApiSqlQueryResults = {
    fields: Record<string, { type: DimensionType }>;
    rows: Record<string, unknown>[];
};

export type PreviewSqlQueryResults = {
    fields: Record<string, { type: DimensionType }>;
    rows: ResultRow[];
};

export type ApiSqlQueryCountResults = {
    count: number;
};

export type ApiScheduledDownloadCsv = {
    jobId: string;
};
export type ApiDownloadCsv = {
    url: string;
    status: SchedulerJobStatus;
    truncated: boolean;
};

export type ProjectCatalog = {
    [database: string]: {
        [schema: string]: {
            [table: string]: Pick<TableBase, 'description' | 'sqlTable'>;
        };
    };
};

export type TablesConfiguration = {
    tableSelection: {
        type: TableSelectionType;
        value: string[] | null;
    };
};

export type CreateProjectMember = {
    email: string;
    role: ProjectMemberRole;
    sendEmail: boolean;
};

export type UpdateProjectMember = {
    role: ProjectMemberRole;
};

export type UpdateMetadata = {
    upstreamProjectUuid?: string | null; // null means we unset this value
};

export type ApiExploresResults = SummaryExplore[];

export type ApiRelationsResults = SummaryRelation[];

export type ApiExploreResults = Omit<Explore, 'unfilteredTables'>;

export type ApiAudienceResults = Audience[];

export type ApiStatusResults = 'loading' | 'ready' | 'error';

export type ApiRefreshResults = {
    jobUuid: string;
};

export type ApiCreatePreviewResults = {
    projectUuid: string;
    compileJobUuid: string;
};

export type ApiJobStartedResults = {
    jobUuid: string;
};

export type ApiCreateUserTokenResults = {
    token: string;
    expiresAt: Date;
};

export type ActivateUser = {
    firstName: string;
    lastName: string;
    password: string;
};

export type CreateUserArgs = {
    firstName: string;
    lastName: string;
    email: Email;
    password: string;
};

export type CreateUserWithRole = {
    firstName: string;
    lastName: string;
    email: Email;
    password?: string;
    role: OrganizationMemberRole;
};

export type ActivateUserWithInviteCode = ActivateUser & {
    inviteCode: string;
};

export type RegisterOrActivateUser =
    | ActivateUserWithInviteCode
    | CreateUserArgs;

export const CompleteUserSchema = z.object({
    organizationName: getOrganizationNameSchema().optional(),
    jobTitle: z.string().min(0),
    enableEmailDomainAccess: z.boolean().default(false),
    isMarketingOptedIn: z.boolean().default(true),
    isTrackingAnonymized: z.boolean().default(false),
});

export type CompleteUserArgs = z.infer<typeof CompleteUserSchema>;

export type UpdateUserArgs = {
    firstName: string;
    lastName: string;
    email: string;
    isMarketingOptedIn: boolean;
    isTrackingAnonymized: boolean;
    isSetupComplete: boolean;
    isActive: boolean;
};

export type PasswordResetLink = {
    expiresAt: Date;
    code: string;
    email: string;
    url: string;
    isExpired: boolean;
};

export type CreatePasswordResetLink = Pick<PasswordResetLink, 'email'>;

export type PasswordReset = {
    code: string;
    newPassword: string;
};

export type ApiHealthResults = HealthState;
export type InviteLink = {
    expiresAt: Date;
    inviteCode: string;
    inviteUrl: string;
    organizationUuid: string;
    userUuid: string;
    email: string;
};
export type CreateInviteLink = Pick<InviteLink, 'expiresAt' | 'email'> & {
    email: string;
    role?: OrganizationMemberRole;
};

export type ApiCreateProjectResults = {
    project: Project;
    hasContentCopy: boolean;
    contentCopyError?: string;
};

export type ProjectSavedChartStatus = boolean;

export type ApiFlashResults = Record<string, string[]>;

export type ApiGeneratePrompt = {
    question: string;
    response: string;
};

export type ApiEventIngestionResponse = {
    status: 'ok' | 'error';
    results: {
        statusCode: number;
        data: EventIngestionData[];
    };
};

export type EventIngestionData = {
    status: 'queued' | 'rejected';
    message: string;
};

export type ApiAiDashboardSummaryResponse = {
    status: 'ok';
    results: DashboardSummary;
};

export type ApiAiGetDashboardSummaryResponse = {
    status: 'ok';
    results: DashboardSummary;
};

export type ApiAiGenerateCustomVizResponse = {
    status: 'ok';
    results: string;
};

type ApiResults =
    | QuestLinkedChatContext
    | QuestMilestoneReport
    | QuestMilestoneReport[]
    | null
    | ApiQueryResults
    | ApiSqlQueryResults
    | ApiCompiledQueryResults
    | ApiExploresResults
    | ApiExploreResults
    | ApiStatusResults
    | ApiRefreshResults
    | ApiCreatePreviewResults
    | ApiHealthResults
    | Organization
    | LightdashUser
    | LoginWith2FAResult
    | LoginOptions
    | SavedChart
    | SavedChart[]
    | Space[]
    | InviteLink
    | OrganizationProject[]
    | Project
    | WarehouseCredentials
    | OrganizationMemberProfile[]
    | ProjectCatalog
    | TablesConfiguration
    | Dashboard
    | DashboardBasicDetails[]
    | OnboardingStatus
    | Dashboard[]
    | DeleteOpenIdentity
    | ApiFlashResults
    | Record<OpenIdIdentitySummary['issuerType'], OpenIdIdentitySummary[]>
    | FilterableField[]
    | DashboardAvailableFilters
    | ProjectSavedChartStatus
    | null
    | Array<unknown>
    | ApiJobStartedResults
    | ApiCreateUserTokenResults
    | CreatePersonalAccessToken
    | PersonalAccessToken
    | ProjectMemberProfile[]
    | ProjectGroupAccess
    | SearchResults
    | Space
    | ShareUrl
    | SlackSettings
    | ApiSlackChannelsResponse['results']
    | UserActivity
    | SchedulerAndTargets
    | SchedulerAndTargets[]
    | FieldValueSearchResult
    | ApiDownloadCsv
    | AllowedEmailDomains
    | UpdateAllowedEmailDomains
    | UserAllowedOrganization[]
    | EmailStatusExpiring
    | ApiScheduledDownloadCsv
    | PinnedItems
    | ViewStatistics
    | SchedulerWithLogs
    | ValidationResponse[]
    | ChartHistory
    | ChartVersion
    | EmbedUrl
    | DecodedEmbed
    | Array<GitRepo>
    | PullRequestCreated
    | GitIntegrationConfiguration
    | UserWarehouseCredentials
    | ApiJobStatusResponse['results']
    | ApiJobScheduledResponse['results']
    | ApiSshKeyPairResponse['results']
    | MostPopularAndRecentlyUpdated
    | ApiCalculateTotalResponse['results']
    | NaturalLanguageSQLResult
    | CreateJoinResponse
    | CompileQueryResponse
    | DownloadCsvResponse
    | GetRelationsResponse
    | GetRelationResponse
    | CreateRelationResponse
    | CompiledRelation
    | CreateRelationTableResponse
    | ApiGeneratedColumnDescription[]
    | ColumnUsageStats
    | (ColumnUsageStats | null)
    | ApiRegenerateColumnLabelResult
    | ApiRegenerateColumnDescriptionResult
    | ApiGenerateColumnSynonymsResult
    | ApiGenerateColumnAiContextResult
    | ApiColumnFillRateResult
    | ApiColumnTopValue[]
    | ApiColumnBasicAnalysisResult
    | CreatePasswordResetLink
    | Record<string, DbtExposure>
    | ApiUpdateRelationTable
    | ApiSuccessEmpty
    | FynoTemplateDetails
    | ChannelIntegrationDetails
    | DimensionConfigurationResult
    | ApiEventIngestionResponse
    | Array<SourceEventKeyMapping>
    | Array<EventMapperSchema>
    | EventMapperSchema
    | AllIntegrationChannels
    | TemplateBasicDetails
    | SourceEventKeyMapping
    | TemplateListWithPagination
    | Template
    | Template[]
    | ProviderTemplateDetails
    | GetAllProviderTemplateResponse
    | Record<string, ColumnInfo>
    | BaseProject
    | ApiCreateComment['results']
    | ApiGetComments['results']
    | ApiDeleteComment
    | ApiCreateProjectResults
    | ApiAiDashboardSummaryResponse['results']
    | ApiAiGetDashboardSummaryResponse['results']
    | CustomAttribute
    | AudienceList
    | ApiCatalogMetadataResults
    | TemplateContent
    | TemplateContent[]
    | WarehouseCatalog
    | undefined
    | RelationSchemaTableResponse
    | TableColumnUsageStatsResponse
    | WarehouseTableInfo
    | FileUploadResponse
    | SubscriptionCategory
    | SubscriptionCategory[]
    | SubscriptionGroup
    | SubscriptionGroup[]
    | SubscriptionGroupWithName
    | SubscriptionGroupWithName[]
    | UserSubscriptionPreference
    | CategoriesWithGroupsAndUserPreference
    | DeliveryControl
    | CustomMaterialisation
    | ApiCatalogAnalyticsResults
    // | ApiAiConversations['results']
    // | ApiAiConversationMessages['results']
    | ApiPromotionChangesResponse['results']
    | SearchWHEntityData
    | UserPreference
    | CreateSourceEventNameKeyMappingResponse
    | CreateEntityAPIKeyResponse
    | Array<EntityApiKey>
    | ApiWarehouseTableFields['results']
    | ApiTogglePinnedItem['results']
    | ApiOrganizationMemberProfiles['results']
    | ApiSqlChart['results']
    | ApiCreateSqlChart['results']
    | ApiUpdateSqlChart['results']
    | ApiContentResponse['results']
    | ApiChartContentResponse['results']
    | UtmKeyMapping
    | UtmKeyMapping[]
    | ApiSqlRunnerJobStatusResponse['results']
    | JourneyDataSchema
    | EventColumn
    | AgentMessage
    | Group
    | GroupWithMembers
    | SyncedTemplateBasicDetails
    | ExternalTemplate
    | TableDescription
    | TableDetails
    | ColumnDetails[]
    | Record<string, boolean>
    | JourneyNodeNameDescriptionResponse
    | ApiCreateVirtualView['results']
    | ApiGithubDbtWritePreview['results']
    | ApiMetricsCatalog['results']
    | ApiMetricsExplorerQueryResults['results']
    | ApiGroupListResponse['results']
    | ApiCreateTagResponse['results']
    | (UserAlertPreferenceResponse | null)[]
    | BeeFreeAuthResponse
    | UserThreadMapping
    | UserThreadList
    | ApiChartAsCodeListResponse['results']
    | ApiDashboardAsCodeListResponse['results']
    | ApiChartAsCodeUpsertResponse['results']
    | ApiGetMetricsTree['results']
    | ApiMetricsExplorerTotalResults['results']
    | UserTraits
    | GenerateCustomAttribute
    | HandlebarNodeType
    | ApiGetSpotlightTableConfig['results']
    | EventLogsStats
    | EventLog
    | EventLogWithoutRawPayload[]
    | IterableListInfo[]
    | SrtCatalog
    | SrtCatalogList
    | SrtCollection
    | SrtCollectionList
    | ApiCalculateSubtotalsResponse['results']
    | ApiExecuteAsyncSqlQueryResults
    | ApiExecuteAsyncDashboardSqlChartQueryResults
    | ApiExecuteAsyncMetricQueryResults
    | ApiExecuteAsyncDashboardChartQueryResults
    | ApiGetAsyncQueryResults
    | ApiSchedulersResponse['results']
    | ApiUserActivityDownloadCsv['results']
    | ExtractVariablesResponse
    | ApiRenameFieldsResponse['results']
    | ApiDownloadAsyncQueryResults
    | ApiDownloadAsyncQueryResultsAsXlsx
    | ApiAiAgentThreadResponse['results']
    | ApiAiAgentThreadMessageVizResponse['results']
    | ApiAiAgentThreadMessageVizQueryResponse['results']
    | ApiUpdateUserAgentPreferencesResponse['results']
    | ApiGetUserAgentPreferencesResponse[`results`]
    | ApiGetProjectParametersResults
    | ApiAiAgentThreadCreateResponse['results']
    | ApiAiAgentThreadMessageCreateResponse['results']
    | IntegrationDetails
    | SenderProfile
    | CampaignList
    | View
    | AiEntitySearchResults
    | Account
    | BeefreeGetAllTemplatesResponse
    | BeefreeGetTemplateByIdResponse
    | BeefreeTemplateCategoriesResponse
    | BeefreeTemplateCategory
    | BeefreeTemplateCollection
    | BeefreeTemplateCollectionsResponse
    | BeefreeTemplateTagsResponse
    | BrazeExportTemplateResponse
    | CampaignQaCreateResponse
    | ContentAnalysisTriggerResponse
    | AudienceMembershipConfig
    | IntelligenceBlockList
    | IntelligenceBlock
    | IntelligenceBlockListWithCreator
    | IntelligenceBlockWithCreator
    | UserMemorySearchResult
    | DirectivesResult
    | PlaybookResult
    | CoreMemoryResult
    | PlaybookDebugCreateResult
    | PlaybookDebugStatus[]
    | RelatedMemoriesResult
    | GetConnectorDestinationResponse
    | AirbyteConnection
    | OpenCodeSession
    | OpenCodeMessage[]
    | OpenCodeSessionInfo[]
    | OpenCodeHealth
    | ApiGetProjectParametersListResults
    | ApiProjectCompileLogResponse['results']
    | ApiProjectCompileLogsResponse['results']
    | ApiUserSchedulersSummaryResponse['results']
    | GeneratedTableCalculation
    | UnusedContent
    | ApiGetChangeResponse['results']
    | ApiAppendInstructionResponse['results']
    | ApiAiAgentThreadGenerateTitleResponse['results']
    | ApiUpdateAiOrganizationSettingsResponse['results']
    | AiArtifact
    | ApiCreateEvaluationResponse['results']
    | ApiAiAgentEvaluationRunSummaryListResponse['results']
    | ApiAiAgentAdminConversationsResponse['results']
    | ApiAiAgentVerifiedArtifactsResponse['results']
    | Kpi[]
    | KpiPreviewResponse
    | AudienceDestinationSync
    | ApiQuestsResponse['results']
    | AgentTask[]
    | Quest
    | Quest[]
    | QuestActivityLog
    | QuestActivityLog[]
    | QuestHandoff
    | QuestHandoff[]
    | QuestHumanInput
    | QuestHumanInput[]
    | QuestHumanInputSummary
    | QuestHumanInputSummary[]
    | OpenQuestHumanInput
    | OpenQuestHumanInput[]
    | QuestWait
    | Quest
    | QuestHandoff
    | QuestHandoff[]
    | QuestWait
    | InternalCampaignQARecord
    | SingleSendExclusionAnalyticsResults
    | PipedreamConnectSession;

export type ApiResponse<T extends ApiResults = ApiResults> = {
    status: 'ok';
    results: T;
};

export type ApiErrorDetail = {
    name: string;
    statusCode: number;
    message: string;
    data: { [key: string]: string };
    sentryTraceId?: string;
    sentryEventId?: string;
};
export type ApiError = {
    status: 'error';
    error: ApiErrorDetail;
};

export const isLightdashMode = (x: string): x is LightdashMode =>
    Object.values<string>(LightdashMode).includes(x);

export enum LightdashInstallType {
    DOCKER_IMAGE = 'docker_image',
    BASH_INSTALL = 'bash_install',
    HEROKU = 'heroku',
    UNKNOWN = 'unknown',
}

export type SentryConfig = {
    backend: {
        dsn: string;
        securityReportUri: string;
    };
    frontend: {
        dsn: string;
    };
    release: string;
    environment: string;
    tracesSampleRate: number;
    profilesSampleRate: number;
    anr: {
        enabled: boolean;
        timeout?: number;
        captureStacktrace: boolean;
    };
};

export type HealthState = {
    healthy: boolean;
    mode: LightdashMode;
    version: string;
    localDbtEnabled: boolean;
    defaultProject?: DbtProjectConfig;
    isAuthenticated: boolean;
    requiresOrgRegistration: boolean;
    hasEmailClient: boolean;
    hasMicrosoftTeams: boolean;
    isServiceAccountEnabled: boolean;
    isOrganizationWarehouseCredentialsEnabled: boolean;
    planTabEnabled: boolean;
    latest: {
        version?: string;
    };
    rudder: {
        writeKey: string | undefined;
        dataPlaneUrl: string | undefined;
    };
    clarity: {
        key: string;
    };
    sentry: Pick<
        SentryConfig,
        | 'frontend'
        | 'release'
        | 'environment'
        | 'tracesSampleRate'
        | 'profilesSampleRate'
    >;
    auth: {
        disablePasswordAuthentication: boolean;
        google: {
            oauth2ClientId: string | undefined;
            loginPath: string;
            googleDriveApiKey: string | undefined;
            enabled: boolean;
            enableGCloudADC: boolean;
        };
        okta: {
            enabled: boolean;
            loginPath: string;
        };
        oneLogin: {
            enabled: boolean;
            loginPath: string;
        };
        azuread: {
            enabled: boolean;
            loginPath: string;
        };
        oidc: {
            enabled: boolean;
            loginPath: string;
        };
        pat: {
            maxExpirationTimeInDays: number | undefined;
        };
        snowflake: {
            enabled: boolean;
        };
        databricks: {
            enabled: boolean;
        };
    };
    posthog:
        | {
              projectApiKey: string;
              feApiHost: string;
              beApiHost: string;
          }
        | undefined;
    siteUrl: string;
    intercom: {
        appId: string;
        apiBase: string;
    };
    pylon: {
        appId: string;
        verificationHash?: string;
    };
    headway: {
        enabled: boolean;
    };
    staticIp: string;
    signupUrl: string | undefined;
    helpMenuUrl: string | undefined;
    query: {
        maxLimit: number;
        defaultLimit: number;
        csvCellsLimit: number;
        maxPageSize: number;
    };
    dashboard: {
        maxTilesPerTab: number;
        maxTabsPerDashboard: number;
    };
    pivotTable: {
        maxColumnLimit: number;
    };
    hasSlack: boolean;
    hasGithub: boolean;
    hasGitlab: boolean;
    hasHeadlessBrowser: boolean;
    hasExtendedUsageAnalytics: boolean;
    hasCacheAutocompleResults: boolean;
    appearance: {
        overrideColorPalette: string[] | undefined;
        overrideColorPaletteName: string | undefined;
    };
    isCustomRolesEnabled: boolean;
    embedding: {
        enabled: boolean;
        events:
            | {
                  enabled: boolean;
                  rateLimiting: {
                      maxEventsPerWindow: number;
                      windowDurationMs: number;
                  };
                  allowedOrigins: string[];
                  enablePostMessage: boolean;
              }
            | undefined;
    };
    ai: {
        analyticsProjectUuid?: string;
        analyticsDashboardUuid?: string;
        isAmbientAiEnabled: boolean;
    };
    echarts6: {
        enabled: boolean;
    };
    funnelBuilder: {
        enabled: boolean;
    };
};

export type CreateProject = Omit<
    Project,
    | 'projectUuid'
    | 'organizationUuid'
    | 'schedulerTimezone'
    | 'createdByUserUuid'
> & {
    warehouseConnection: CreateWarehouseCredentials;
    copyWarehouseConnectionFromUpstreamProject?: boolean;
    tableConfiguration?: CreateProjectTableConfiguration;
    copyContent?: boolean;
};

export type CreateProjectOptionalCredentials = Omit<
    CreateProject,
    'warehouseConnection'
> & {
    warehouseConnection?: CreateWarehouseCredentials;
};

export type UpdateProject = Omit<
    Project,
    | 'projectUuid'
    | 'organizationUuid'
    | 'type'
    | 'schedulerTimezone'
    | 'createdByUserUuid'
> & {
    warehouseConnection: CreateWarehouseCredentials;
};

export type UpdateProjectSettings = Partial<Pick<Project, 'name' | 'timezone'>>;

export type UploadCSVFileResponse = {
    uploadId: string;
    originalname: string;
    size: number;
    filename: string;
    counts: {
        total: number;
        valid: number;
        invalid: number;
        duplicate: number;
    };
    columns: string[];
    destColumn: string[];
    batchSize: number[];
    chunks: {
        chunk1: number;
    };
    blobPath: string;
};

export type UploadPresignedUrl = {
    id: string;
};

export type ProjectSyncPayload = {
    cron: string;
};

export type MaterializationCleanupSchedulePayload = {
    cron: string;
    olderThanDays: number;
};

export type ProjectUnsubscribeIdentifierPayload = {
    unsubscribeIdentifier: UnsubscribeIdTypes;
};

export interface IterableCreateStaticListRequest {
    description: string;
    name: string;
}

export interface IterableCreateStaticListResponse {
    listId: number;
}
export interface IterableSubscriber {
    userId: string;
    email?: string;
    dataFields?: Record<string, unknown>;
    mergeNestedObjects?: boolean;
    preferUserId?: boolean;
}
export interface IterableListSubscribeRequest {
    listId: number;
    subscribers: IterableSubscriber[];
    updateExistingUsersOnly?: boolean;
}

export interface IterableListInfo {
    createdAt: Date;
    description: string;
    id: number;
    listType: string;
    name: string;
}
interface FailedUpdates {
    conflictEmails: string[];
    conflictUserIds: string[];
    forgottenEmails: string[];
    forgottenUserIds: string[];
    invalidDataEmails: string[];
    invalidDataUserIds: string[];
    invalidEmails: string[];
    invalidUserIds: string[];
    notFoundEmails: string[];
    notFoundUserIds: string[];
}

export interface IterableListSubscribeResponse {
    createdFields: string[];
    failCount: number;
    failedUpdates: FailedUpdates;
    filteredOutFields: string[];
    invalidEmails: string[];
    invalidUserIds: string[];
    successCount: number;
}

export type ApiIterableListSubscribeResponse = {
    status: string;
    result: IterableListSubscribeResponse[];
};

export type ApiIterableGetListResponse = {
    status: string;
    results: IterableListInfo[];
};

export const getResultValueArray = (
    rows: ResultRow[],
    preferRaw: boolean = false,
    calculateMinAndMax: boolean = false,
    excludeNulls: boolean = false,
): {
    results: Record<string, unknown>[];
    minsAndMaxes?: Record<string, { min: number; max: number }>;
} => {
    const minMax: Record<string, { min: number; max: number }> = {};

    const results = rows.map((row) =>
        Object.keys(row).reduce<Record<string, unknown>>((acc, key) => {
            if (excludeNulls && row[key]?.value.raw === null) {
                return acc;
            }
            const rawWithFallback =
                row[key]?.value.raw ?? row[key]?.value.formatted; // using nullish coalescing operator to handle null and undefined only
            const formattedWithFallback =
                row[key]?.value.formatted || row[key]?.value.raw;

            const value = preferRaw ? rawWithFallback : formattedWithFallback;

            acc[key] = value;

            if (calculateMinAndMax) {
                const numericValue = Number(value);
                if (!Number.isNaN(numericValue)) {
                    if (!minMax[key]) {
                        minMax[key] = { min: numericValue, max: numericValue };
                    } else {
                        minMax[key].min = Math.min(
                            minMax[key].min,
                            numericValue,
                        );
                        minMax[key].max = Math.max(
                            minMax[key].max,
                            numericValue,
                        );
                    }
                }
            }

            return acc;
        }, {}),
    );

    return calculateMinAndMax ? { results, minsAndMaxes: minMax } : { results };
};

export const getDateGroupLabel = (axisItem: ItemsMap[string]) => {
    if (
        isDimension(axisItem) &&
        [DimensionType.DATE, DimensionType.TIMESTAMP].includes(axisItem.type) &&
        (axisItem.group || (axisItem.groups && axisItem.groups.length > 0)) &&
        axisItem.label &&
        axisItem.timeInterval
    ) {
        const timeFrame =
            TimeFrames[axisItem.timeInterval]?.toLowerCase() || '';

        if (timeFrame && axisItem.label.endsWith(` ${timeFrame}`)) {
            // Remove the time frame from the end of the label - e.g. from 'Order created day' to 'Order created'.
            return getItemLabelWithoutTableName(axisItem).replace(
                new RegExp(`\\s+${timeFrame}$`),
                '',
            );
        }

        return friendlyName(axisItem.label);
    }

    return undefined;
};

export const getAxisName = ({
    isAxisTheSameForAllSeries,
    selectedAxisIndex,
    axisReference,
    axisIndex,
    axisName,
    series,
    itemsMap,
}: {
    isAxisTheSameForAllSeries: boolean;
    selectedAxisIndex: number;
    axisReference: 'yRef' | 'xRef';
    axisIndex: number;
    axisName?: string;
    series?: Series[];
    itemsMap: ItemsMap | undefined;
}): string | undefined => {
    const itemIndex = (series || [])[0]?.encode[axisReference].field;
    const defaultItem = itemsMap && itemIndex ? itemsMap[itemIndex] : undefined;
    const dateGroupName = defaultItem
        ? getDateGroupLabel(defaultItem)
        : undefined;
    const fallbackSeriesName: string | undefined =
        series && series.length === 1
            ? series[0]?.name ||
              (defaultItem && getItemLabelWithoutTableName(defaultItem))
            : undefined;

    return !isAxisTheSameForAllSeries || selectedAxisIndex === axisIndex
        ? axisName || dateGroupName || fallbackSeriesName
        : undefined;
};

export function getFieldMap(
    explore: Explore,
    additionalMetrics: AdditionalMetric[] = [],
): Record<string, CompiledField | AdditionalMetric> {
    return [...getFields(explore), ...additionalMetrics].reduce(
        (sum, field) => ({
            ...sum,
            [getItemId(field)]: field,
        }),
        {},
    );
}

export function getItemMap(
    explore: Explore,
    additionalMetrics: AdditionalMetric[] = [],
    tableCalculations: TableCalculation[] = [],
    customDimensions: CustomDimension[] = [],
): ItemsMap {
    const convertedAdditionalMetrics = (additionalMetrics || []).reduce<
        Metric[]
    >((acc, additionalMetric) => {
        const table = explore.tables[additionalMetric.table];
        if (table) {
            const metric = convertAdditionalMetric({
                additionalMetric,
                table,
            });
            acc.push(metric);
        }
        return acc;
    }, []);
    return [
        ...getFields(explore),
        ...convertedAdditionalMetrics,
        ...tableCalculations,
        ...customDimensions,
    ].reduce((acc, item) => {
        acc[getItemId(item)] = item;
        return acc;
    }, {} as ItemsMap);
}

export const getFieldsFromMetricQuery = (
    metricQuery: MetricQuery,
    explore: Explore,
): ItemsMap => {
    const itemsMap = getItemMap(
        explore,
        metricQuery.additionalMetrics,
        metricQuery.tableCalculations,
        metricQuery.customDimensions,
    );
    const itemIdsInMetricQuery = [
        ...metricQuery.dimensions,
        ...metricQuery.metrics,
        ...(metricQuery.tableCalculations || []).map(getItemId),
    ];
    return itemIdsInMetricQuery.reduce<ItemsMap>((acc, id) => {
        const item = itemsMap[id];
        if (item) acc[id] = item;
        return acc;
    }, {});
};

export const getDimensionsFromItemsMap = (itemsMap: ItemsMap) =>
    Object.entries(itemsMap).reduce<
        Record<string, Dimension | CustomDimension>
    >((acc, [key, value]) => {
        if (isDimension(value) || isCustomDimension(value)) {
            return { ...acc, [key]: value };
        }
        return acc;
    }, {});

export const getFilterableDimensionsFromItemsMap = (itemsMap: ItemsMap) =>
    Object.entries(itemsMap).reduce<Record<string, FilterableDimension>>(
        (acc, [key, value]) => {
            if (isDimension(value) && isFilterableDimension(value)) {
                return { ...acc, [key]: value };
            }
            return acc;
        },
        {},
    );

export const getMetricsFromItemsMap = (
    itemsMap: ItemsMap,
    filter: (value: ItemsMap[string]) => boolean = () => true,
) =>
    Object.entries(itemsMap).reduce<Record<string, Metric>>(
        (acc, [key, value]) => {
            if (isField(value) && isMetric(value) && filter(value)) {
                return { ...acc, [key]: value };
            }
            return acc;
        },
        {},
    );

export const getTableCalculationsFromItemsMap = (itemsMap?: ItemsMap) =>
    Object.entries(itemsMap ?? {}).reduce<Record<string, TableCalculation>>(
        (acc, [key, value]) => {
            if (isTableCalculation(value)) {
                return { ...acc, [key]: value };
            }
            return acc;
        },
        {},
    );

export function itemsInMetricQuery(
    metricQuery: MetricQuery | undefined,
): string[] {
    return metricQuery === undefined
        ? []
        : [
              ...metricQuery.metrics,
              ...metricQuery.dimensions,
              ...(metricQuery.tableCalculations || []).map((tc) => tc.name),
          ];
}

export function formatRawValue(
    field: Field | Metric | TableCalculation | CustomDimension | undefined,
    value: AnyType,
) {
    const isTimestamp =
        isField(field) &&
        (field.type === DimensionType.DATE ||
            field.type === DimensionType.TIMESTAMP);

    if (isTimestamp && value !== null) {
        // We want to return the datetime in UTC to avoid timezone issues in the frontend like in chart tooltips
        return dayjs(value).utc(true).format();
    }

    return value;
}

// ! We format raw values so we can't use the values directly from the warehouse to compare with subtotals of date dimensions
export function formatRawRows(
    rows: { [col: string]: AnyType }[],
    itemsMap: ItemsMap,
): Record<string, unknown>[] {
    return rows.map((row) => {
        const resultRow: ResultRow = {};
        const columnNames = Object.keys(row || {});

        for (const columnName of columnNames) {
            const value = row[columnName];
            const item = itemsMap[columnName];

            resultRow[columnName] = formatRawValue(item, value);
        }

        return resultRow;
    });
}
/**
 * Deformat the result row to ensure the values are in the correct format.
 * @param object - The result row to deformat.
 * @returns The deformatted result row.
 */
export const deformatResultRow = (object: ResultRow) =>
    Object.keys(object).reduce(
        (acc, key) => {
            acc[key] = object[key].value.raw;
            return acc;
        },
        {} as Record<string, unknown>,
    );

export function formatRow(
    row: { [col: string]: AnyType },
    itemsMap: ItemsMap,
    pivotValuesColumns?: Record<string, PivotValuesColumn> | null,
    parameters?: Record<string, unknown>,
): ResultRow {
    const resultRow: ResultRow = {};
    const columnNames = Object.keys(row || {});

    for (const columnName of columnNames) {
        const value = row[columnName];
        const pivotValuesColumn = pivotValuesColumns?.[columnName];
        const item = itemsMap[pivotValuesColumn?.referenceField ?? columnName];

        resultRow[columnName] = {
            value: {
                raw: formatRawValue(item, value),
                formatted: formatItemValue(item, value, false, parameters),
            },
        };
    }

    return resultRow;
}

export function formatRows(
    rows: { [col: string]: AnyType }[],
    itemsMap: ItemsMap,
    pivotValuesColumns?: Record<string, PivotValuesColumn> | null,
    parameters?: Record<string, unknown>,
): ResultRow[] {
    return rows.map((row) =>
        formatRow(row, itemsMap, pivotValuesColumns, parameters),
    );
}

const isObject = (object: AnyType) =>
    object != null && typeof object === 'object';
export const removeEmptyProperties = (object: Record<string, AnyType>) => {
    const newObj: Record<string, AnyType> = {};
    Object.keys(object).forEach((key) => {
        if (object[key] === Object(object[key]))
            newObj[key] = removeEmptyProperties(object[key]);
        else if (object[key] !== undefined && object[key] !== null)
            newObj[key] = object[key];
    });
    return newObj;
};
export const deepEqual = (
    object1: Record<string, AnyType>,
    object2: Record<string, AnyType>,
): boolean => {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);
    if (keys1.length !== keys2.length) {
        return false;
    }
    return keys1.every((key) => {
        const val1: AnyType = object1[key];
        const val2: AnyType = object2[key];
        const areObjects = isObject(val1) && isObject(val2);
        return !(
            (areObjects && !deepEqual(val1, val2)) ||
            (!areObjects && val1 !== val2)
        );
    });
};

export type ModifierDetails = {
    by: string;
    at: Date;
};
export const getMaterialisationName = (
    entityID: string,
    materializationType: string,
    whType: WarehouseTypes,
): string => {
    const prefix = 'srt';
    const uuidParts = entityID.split('-');
    const timestamp = new Date().toISOString().replace(/[-:.]/g, '');
    const generatedName = `${prefix}_${materializationType}_${timestamp}_${uuidParts[0]}`;
    return getSortmentWarehouseName(generatedName, whType);
};

/**
 * Parse generated names for warehouse cleanup; unknown names are left alone.
 * Types are normalized to lowercase and must be compared exactly, since
 * `campaign_exclude_audience_snapshot` is distinct from `campaign`.
 */
export const parseMaterialisationName = (
    tableName: string,
):
    | {
          materializationType: string;
          entityIdPrefix: string;
          createdAt: Date;
      }
    | undefined => {
    // srt_<type>_<YYYYMMDD>T<HHMMSSmmm>Z_<entity id prefix>
    // `<type>` may itself contain underscores, so the timestamp -- which has a
    // fixed shape -- is what the pattern anchors on rather than the separators.
    const match = /^srt_(.+)_(\d{8})T(\d{9})Z_([0-9a-f]+)$/i.exec(tableName);
    if (!match) return undefined;

    const [, materializationType, datePart, timePart, entityIdPrefix] = match;
    const iso = `${datePart.slice(0, 4)}-${datePart.slice(
        4,
        6,
    )}-${datePart.slice(6, 8)}T${timePart.slice(0, 2)}:${timePart.slice(
        2,
        4,
    )}:${timePart.slice(4, 6)}.${timePart.slice(6, 9)}Z`;
    const createdAt = new Date(iso);
    if (Number.isNaN(createdAt.getTime())) return undefined;

    return {
        materializationType: materializationType.toLowerCase(),
        entityIdPrefix: entityIdPrefix.toLowerCase(),
        createdAt,
    };
};
export const getProjectDirectory = (
    dbtConnection?: DbtProjectConfig,
): string | undefined => {
    if (!dbtConnection) return undefined;

    switch (dbtConnection.type) {
        case DbtProjectType.DBT:
            return dbtConnection.project_dir;
        case DbtProjectType.GITHUB:
        case DbtProjectType.GITLAB:
        case DbtProjectType.BITBUCKET:
        case DbtProjectType.AZURE_DEVOPS:
            return dbtConnection.project_sub_path;
        case DbtProjectType.DBT_CLOUD_IDE:
        case DbtProjectType.NONE:
            return undefined;
        default:
            return undefined;
    }
};

export const getVisibleTables = (
    relationData: CompiledRelation,
    projectUuid: string,
    warehouseType: WarehouseTypes,
): CompiledRelation => {
    // Identify the primary table
    const primaryTable = Object.values(relationData.tables).find(
        (table) => table.type === RelationTableType.PRIMARY,
    );

    if (!primaryTable) {
        return relationData;
    }

    // Filter the keys in the tables object
    const visibleTables = Object.keys(relationData.tables).reduce<
        Record<string, CompiledRelationTable>
    >((acc, key) => {
        const table = relationData.tables[key];
        // Exempt tables with isReserved and not deduped tables set to true a
        if (
            table.isReserved &&
            !table.name.includes(
                getDedupedEventTableName(projectUuid, warehouseType),
            ) &&
            !table.name.includes(
                getJourneyEventsTableName(projectUuid, warehouseType),
            ) &&
            table.type !== RelationTableType.CUSTOM_SQL &&
            !table.name.includes(
                getDedupedSubscriptionEventTableName(
                    projectUuid,
                    warehouseType,
                ),
            ) &&
            !table.name.includes(getEventTableName(projectUuid, warehouseType))
        ) {
            return acc;
        }

        // Check if the key exists in the joinedTables array with the correct source and target
        const isKeyInJoinedTables = relationData.joinedTables.some(
            (joinedTable) => joinedTable.table === key,
        );
        // If the key exists in joinedTables, add it to the result
        if (isKeyInJoinedTables) {
            acc[key] = relationData.tables[key];
        }

        return acc;
    }, {});

    return {
        ...relationData,
        tables: { ...visibleTables, [primaryTable.name]: primaryTable },
    };
};

export const getSentryTags = (
    user: LightdashUser,
    packageType: SentryPackageType,
): Record<SentryTagKeys, string> => {
    const tags: Record<SentryTagKeys, string> = {
        [SentryTagKeys.organizationUuid]: user.organizationUuid ?? '',
        [SentryTagKeys.userUuid]: user.userUuid,
        [SentryTagKeys.organizationName]: user.organizationName ?? '',
        [SentryTagKeys.userName]: `${user.firstName} ${user.lastName}`,
        [SentryTagKeys.userEmail]: user.email ?? '',
        [SentryTagKeys.package]: packageType,
        [SentryTagKeys.errorGroup]: '',
        [SentryTagKeys.sendToPagerDuty]: 'false',
    };
    return tags;
};
export function isNotNull<T>(arg: T): arg is Exclude<T, null> {
    return arg !== null;
}

export type TreeCreateSpace = CreateSpace & {
    children?: TreeCreateSpace[];
    groupAccess?: {
        groupUuid: string;
        role: SpaceMemberRole;
    }[];
};

export const SPACE_TREE_1: TreeCreateSpace[] = [
    {
        name: 'Parent Space 1',
        children: [
            {
                name: 'Child Space 1.1',
                children: [
                    {
                        name: 'Grandchild Space 1.1.1',
                    },
                    {
                        name: 'Grandchild Space 1.1.2',
                    },
                ],
            },
            {
                name: 'Child Space 1.2',
                children: [
                    {
                        name: 'Grandchild Space 1.2.1',
                    },
                    {
                        name: 'Grandchild Space 1.2.2',
                        children: [
                            {
                                name: 'Great Grandchild Space 1.2.2.1',
                            },
                        ],
                    },
                ],
            },
            {
                name: 'Child Space 1.3',
                children: [
                    {
                        name: 'Grandchild Space 1.3.1',
                        children: [
                            {
                                name: 'Great Grandchild Space 1.3.1.1',
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        name: 'Parent Space 2',
        isPrivate: true,
        children: [
            {
                name: 'Child Space 2.1',
                children: [
                    {
                        name: 'Grandchild Space 2.1.1',
                    },
                ],
            },
        ],
    },
    {
        name: 'Parent Space 3',
        isPrivate: true,
        access: [
            // Admin will automatically be added, we only seed editor
            {
                userUuid: SEED_ORG_1_EDITOR.user_uuid,
                role: SpaceMemberRole.EDITOR,
            },
        ],
        children: [
            {
                name: 'Child Space 3.1',
            },
        ],
    },
    // Created by admin and added group access
    {
        name: 'Parent Space 5',
        isPrivate: true,
        access: [],
        groupAccess: [
            {
                groupUuid: SEED_GROUP_2.groupUuid,
                role: SpaceMemberRole.EDITOR,
            },
        ],
        children: [
            {
                name: 'Child Space 5.1',
            },
        ],
    },
] as const;

export const SPACE_TREE_2: TreeCreateSpace[] = [
    {
        name: 'Parent Space 4',
        isPrivate: true,
        access: [],
        children: [
            {
                name: 'Child Space 4.1',
            },
        ],
    },
] as const;

export { Liquid } from 'liquidjs';

export * from './types/questMilestoneReport';

export * from './types/questLinkedChat';

export * from './types/questDisplay';
